/**
 * Content Service - Static content for all pregnancy app features
 * No AI/Gemini dependencies - all hand-crafted content
 * 
 * SOURCES & REFERENCES:
 * - Food Safety: FDA (fda.gov/food/people-risk-foodborne-illness/pregnant-women)
 * - Baby Development: American Pregnancy Association (americanpregnancy.org)
 * - Mercury in Fish: EPA/FDA (epa.gov/fish-tech/2017-epa-fda-advice-about-eating-fish)
 * - Pregnancy Milestones: ACOG (acog.org/womens-health/pregnancy)
 * - Partner Support: March of Dimes (marchofdimes.org/pregnancy/partners)
 */

import { DailyGuidance, DailyCheckIn, MilestoneInfo, ChecklistItem, FoodSafetyResult, NameIdea, QuestionItem, BirthPlan, BirthPlanSection, FlashFact } from "../types";
import { getPartnerTipForWeek } from "../constants/partnerCoaching";
import { getPregnancyTipForWeek } from "../constants/pregnancyContent";
import { ALL_FLASH_FACTS, getFactsForWeekAndRole } from "../constants/flashFacts";
import { MILESTONE_DATA as MILESTONE_ARRAY, getFullMilestoneData, MilestoneData } from "../constants/milestoneData";

// --- BABY SIZE COMPARISONS ---
export const BABY_SIZES: Record<string, string[]> = {
    fruit: [
        "Poppy Seed", "Sesame Seed", "Lentil", "Blueberry", "Raspberry",
        "Cherry", "Olive", "Grape", "Fig", "Lime",
        "Lemon", "Plum", "Peach", "Apple", "Avocado",
        "Onion", "Mango", "Banana", "Pomegranate", "Papaya",
        "Grapefruit", "Cantaloupe", "Cauliflower", "Coconut", "Pineapple",
        "Butternut Squash", "Honeydew", "Cabbage", "Watermelon", "Pumpkin"
    ],
    geek: [
        "8-bit Pixel", "Microchip", "SD Card", "D4 Die", "D20 Die",
        "USB Drive", "GameBoy Cartridge", "Mouse", "Smartphone", "GameBoy Color",
        "Tricorder", "Phaser", "Sonic Screwdriver", "Pip-Boy", "Lightsaber Hilt",
        "VR Headset", "Droid", "Keyboard", "Laptop", "Switch Console",
        "Desktop Tower", "Monitor", "Server Rack", "Gaming PC", "Arcade Cabinet"
    ],
    sports: [
        "BB Pellet", "Airsoft Pellet", "Marble", "Golf Tee", "Poker Chip",
        "Golf Ball", "Squash Ball", "Ping Pong Ball", "Billiard Ball", "Tennis Ball",
        "Baseball", "Cricket Ball", "Softball", "Hockey Puck", "Curling Stone",
        "Volleyball", "Soccer Ball", "Bowling Ball", "Basketball", "Beach Ball",
        "Medicine Ball", "Exercise Ball", "Yoga Ball", "Punching Bag", "Giant Beachball"
    ],
    tools: [
        "Small Washer", "Screw Head", "Nut", "Bolt", "Allen Key",
        "Socket", "Spark Plug", "Tape Measure", "Screwdriver Handle", "Wrench",
        "Pliers", "Hammer Head", "Drill Battery", "Multimeter", "Angle Grinder",
        "Circular Saw Blade", "Drill", "Toolbox", "Shop Vac", "Compressor",
        "Generator", "Table Saw", "Band Saw", "Welder", "Industrial Lathe"
    ],
    snacks: [
        "Tic Tac", "Sprinkle", "Skittle", "M&M", "Gummy Bear",
        "Oreo", "Reese's Cup", "Granola Bar", "Pop-Tart", "Donut",
        "Bagel", "Croissant", "Burrito", "Sub Sandwich", "Large Pizza Slice",
        "Bucket of Popcorn", "Party Size Chips", "Sheet Cake", "Whole Watermelon",
        "Thanksgiving Turkey", "Wedding Cake Tier", "Giant Cookie", "Party Platter", "Whole Ham", "Roast"
    ]
};

// --- MILESTONE DATA ---
export const MILESTONE_DATA: Record<string, MilestoneInfo> = {
    "Positive Test": {
        title: "Positive Test & First Call",
        description: "The journey begins! Hormones like HCG are doubling rapidly. It's a mix of excitement and 'what now?' for everyone involved.",
        partnerTip: "Celebrate in a low-pressure way. Ask if she wants to keep it secret for now. Help research OB/Midwives.",
        logistics: "Schedule the first appointment (usually occurs around 8-10 weeks)."
    },
    "First Ultrasound": {
        title: "First Ultrasound (Dating Scan)",
        description: "You'll likely see a tiny 'bean' and hear the heartbeat for the first time. This confirms the due date and viability.",
        partnerTip: "It's emotional. Hold her hand. If allowed, record the heartbeat sound on your phone - you'll treasure it.",
        logistics: "Duration: 30 mins. Drink water beforehand (full bladder helps visibility early on)."
    },
    "Nuchal Translucency": {
        title: "Nuchal Translucency Scan",
        description: "A screening for chromosomal abnormalities. It measures the fluid at the back of the baby's neck.",
        partnerTip: "Anxiety can be high before results. Plan a distraction (lunch or a walk) immediately after the appointment.",
        logistics: "Usually between 11-13 weeks. Results may take a few days."
    },
    "Second Trimester": {
        title: "Second Trimester Begins",
        description: "The 'Golden Period'! Energy often returns, nausea subsides, and the bump starts showing.",
        partnerTip: "Great time for a 'Babymoon' or starting the nursery. She might feel more like herself again.",
        logistics: "Weeks 14-27. Monthly checkups continue."
    },
    "Anatomy Scan": {
        title: "Anatomy Scan (20 Week)",
        description: "The big one! Detailed look at all organs, limbs, spine, and brain. Usually when you can find out the gender if you want.",
        partnerTip: "Don't miss this one. It's the longest scan and you'll see the face! Bring tissues.",
        logistics: "Duration: 45-60 mins. Around week 20. May need a follow-up if baby isn't cooperating."
    },
    "Glucose Test": {
        title: "Glucose Screening Test",
        description: "Screening for Gestational Diabetes. She drinks a sugary liquid and waits an hour for blood draw.",
        partnerTip: "The drink can make her feel gross/jittery/nauseous. Bring a protein snack for AFTER the blood draw.",
        logistics: "Week 24-28. One-hour test first; if failed, three-hour test follows."
    },
    "Third Trimester": {
        title: "Third Trimester Begins",
        description: "The home stretch! Baby grows fast, and physical discomfort increases (back pain, heartburn, swelling).",
        partnerTip: "Step up household chores without being asked. Shoe-tying assistance may literally be needed.",
        logistics: "Week 28+. Visits increase to every 2 weeks, then weekly near the end."
    },
    "Tdap Vaccine": {
        title: "Tdap Vaccine",
        description: "Protects the baby from Whooping Cough (Pertussis) after birth before they can get vaccinated.",
        partnerTip: "You (the partner) need one too! Get yours so you're 'cocooning' and protecting the baby.",
        logistics: "Week 27-36. Ideally early in window to maximize antibody transfer."
    },
    "Baby Shower": {
        title: "Baby Shower / Prep",
        description: "Gathering gear and celebrating! It can be overwhelming with gifts and attention.",
        partnerTip: "Be the 'Load Master'. Transport gifts, write down who gave what for thank-you notes.",
        logistics: "Usually Week 30-34. Have registry ready beforehand."
    },
    "Group B Strep": {
        title: "Group B Strep Test",
        description: "A simple swab to check for GBS bacteria. If positive, antibiotics during labor protect baby.",
        partnerTip: "It's a quick test. Results are straightforward - positive just means antibiotics during labor.",
        logistics: "Week 36-37. Results back in a few days."
    },
    "Hospital Bag": {
        title: "Pack Hospital Bag",
        description: "Get the bag ready! Ideally done by week 37 just in case baby comes early.",
        partnerTip: "Pack your own bag too! Snacks, long charger cables, pillow, change of clothes, toiletries.",
        logistics: "Week 36-37. Keep bags by the door or in the car."
    },
    "Due Date": {
        title: "Due Date Week",
        description: "40 Weeks! But only 5% of babies arrive exactly on their due date. It's really a 'due window'.",
        partnerTip: "Patience is key. Plan fun distractions. Don't ask 'Is it happening yet?' every hour. Trust the process.",
        logistics: "Week 40. Induction discussions usually happen if you go past 41 weeks."
    }
};

// --- COMPREHENSIVE FOOD SAFETY DATABASE ---
export const FOOD_DATABASE: Record<string, FoodSafetyResult> = {
    // FISH & SEAFOOD
    "salmon": { item: "Salmon", status: "Safe", reason: "Excellent choice! Low in mercury, high in omega-3s which support baby's brain development. Eat 2-3 servings of low-mercury fish weekly.", alternatives: "", source: "FDA/EPA", learnMoreUrl: "https://www.fda.gov/food/consumers/advice-about-eating-fish" },
    "tuna": { item: "Tuna", status: "Caution", reason: "Light canned tuna is safer (lower mercury). Limit albacore/white tuna to 6oz per week. Avoid bigeye tuna entirely.", alternatives: "Salmon, sardines, or light canned tuna", source: "FDA/EPA", learnMoreUrl: "https://www.fda.gov/food/consumers/advice-about-eating-fish" },
    "sushi": { item: "Sushi (Raw)", status: "Avoid", reason: "Raw fish carries parasite and bacterial risks. Even 'sushi-grade' isn't regulated for pregnant women.", alternatives: "Cooked rolls (California, shrimp tempura, eel), veggie rolls, or sashimi-style cooked fish", source: "FDA", learnMoreUrl: "https://www.fda.gov/food/people-risk-foodborne-illness/food-safety-moms-be" },
    "shrimp": { item: "Shrimp", status: "Safe", reason: "Low in mercury and high in protein. Great choice when fully cooked.", alternatives: "" },
    "crab": { item: "Crab", status: "Safe", reason: "Real crab is safe when cooked. Imitation crab (surimi) is also safe.", alternatives: "" },
    "lobster": { item: "Lobster", status: "Safe", reason: "Low mercury and safe when fully cooked.", alternatives: "" },
    "oysters": { item: "Oysters (Raw)", status: "Avoid", reason: "Raw oysters can contain harmful bacteria and viruses. Cook thoroughly.", alternatives: "Cooked/grilled oysters, clams, or mussels", source: "FDA", learnMoreUrl: "https://www.fda.gov/food/people-risk-foodborne-illness/selecting-and-serving-fresh-and-frozen-seafood-safely" },
    "swordfish": { item: "Swordfish", status: "Avoid", reason: "Very high in mercury. Avoid entirely during pregnancy.", alternatives: "Salmon, tilapia, cod, or shrimp", source: "FDA/EPA", learnMoreUrl: "https://www.fda.gov/food/consumers/advice-about-eating-fish" },
    "shark": { item: "Shark", status: "Avoid", reason: "Extremely high in mercury. Do not eat during pregnancy.", alternatives: "Any low-mercury fish", source: "FDA/EPA", learnMoreUrl: "https://www.fda.gov/food/consumers/advice-about-eating-fish" },
    "tilapia": { item: "Tilapia", status: "Safe", reason: "Very low in mercury. Mild taste, easy to prepare.", alternatives: "" },
    "cod": { item: "Cod", status: "Safe", reason: "Low mercury, good source of lean protein.", alternatives: "" },
    "catfish": { item: "Catfish", status: "Safe", reason: "Low in mercury, farm-raised is fine.", alternatives: "" },
    "anchovies": { item: "Anchovies", status: "Safe", reason: "Small fish = low mercury. Great source of omega-3s.", alternatives: "" },
    "sardines": { item: "Sardines", status: "Safe", reason: "Excellent! Small fish with very low mercury and high omega-3s.", alternatives: "" },
    "mackerel": { item: "Mackerel", status: "Caution", reason: "King mackerel is HIGH in mercury (avoid). Atlantic mackerel is lower and okay in moderation.", alternatives: "Atlantic mackerel only, or choose salmon" },
    "smoked salmon": { item: "Smoked Salmon (Lox)", status: "Caution", reason: "Cold-smoked salmon carries slight Listeria risk. Hot-smoked or heated until steaming is safer.", alternatives: "Canned salmon, cooked salmon, or heated smoked salmon", source: "FDA", learnMoreUrl: "https://www.fda.gov/food/people-risk-foodborne-illness/listeria-and-pregnancy" },

    // MEAT
    "steak": { item: "Steak", status: "Caution", reason: "Must be cooked to at least medium (145°F internal). Pink is risky - toxoplasmosis and bacteria concerns.", alternatives: "Well-done steak, or slow-cooked beef dishes", source: "USDA", learnMoreUrl: "https://www.fsis.usda.gov/food-safety/safe-food-handling-and-preparation/food-safety-basics/safe-minimum-internal-temperature" },
    "chicken": { item: "Chicken", status: "Safe", reason: "Safe when fully cooked to 165°F. No pink in the middle.", alternatives: "" },
    "pork": { item: "Pork", status: "Safe", reason: "Safe when cooked to 145°F with 3-minute rest. Avoid pink pork.", alternatives: "" },
    "bacon": { item: "Bacon", status: "Safe", reason: "Safe when cooked crispy. The cooking kills any concerns.", alternatives: "" },
    "deli meat": { item: "Deli Meat / Cold Cuts", status: "Caution", reason: "Listeria risk from cold deli meats. Must be heated until steaming hot (165°F) before eating.", alternatives: "Fresh-sliced rotisserie chicken, or deli meat heated in a pan", source: "FDA", learnMoreUrl: "https://www.fda.gov/food/people-risk-foodborne-illness/listeria-and-pregnancy" },
    "hot dog": { item: "Hot Dogs", status: "Caution", reason: "Processed meat with Listeria risk. Must be heated until steaming hot.", alternatives: "Grilled chicken sausage, or very well-cooked hot dogs", source: "FDA", learnMoreUrl: "https://www.fda.gov/food/people-risk-foodborne-illness/listeria-and-pregnancy" },
    "ham": { item: "Ham", status: "Caution", reason: "Pre-packaged ham should be heated until steaming. Fresh-cooked ham is fine.", alternatives: "Freshly baked ham" },
    "pepperoni": { item: "Pepperoni", status: "Caution", reason: "If on pizza, the oven heat makes it safe. Cold pepperoni should be avoided.", alternatives: "Pizza with pepperoni (cooked), or other cooked toppings" },
    "salami": { item: "Salami", status: "Caution", reason: "Cured meats carry slight risk. Heat until steaming if possible.", alternatives: "Cooked Italian meats" },
    "beef jerky": { item: "Beef Jerky", status: "Safe", reason: "Commercial jerky is safe - the drying and salting process kills pathogens.", alternatives: "" },
    "liver": { item: "Liver", status: "Avoid", reason: "Very high in Vitamin A, which can cause birth defects in large amounts.", alternatives: "Other lean meats for iron", source: "ACOG", learnMoreUrl: "https://www.acog.org/womens-health/faqs/nutrition-during-pregnancy" },
    "pate": { item: "Pâté", status: "Avoid", reason: "Contains liver (high Vitamin A) and is refrigerated (Listeria risk).", alternatives: "Hummus, bean dips", source: "FDA", learnMoreUrl: "https://www.fda.gov/food/people-risk-foodborne-illness/listeria-and-pregnancy" },

    // DAIRY & EGGS
    "cheese": { item: "Soft Cheese (Brie, Feta, etc.)", status: "Caution", reason: "Must be made from PASTEURIZED milk. Unpasteurized soft cheese can contain Listeria.", alternatives: "Hard cheeses (cheddar, parmesan), or pasteurized soft cheese (check label)", source: "FDA", learnMoreUrl: "https://www.fda.gov/food/people-risk-foodborne-illness/listeria-and-pregnancy" },
    "brie": { item: "Brie", status: "Caution", reason: "Safe only if made from pasteurized milk. Check the label.", alternatives: "Pasteurized brie, or hard cheeses", source: "FDA", learnMoreUrl: "https://www.fda.gov/food/people-risk-foodborne-illness/listeria-and-pregnancy" },
    "feta": { item: "Feta", status: "Caution", reason: "Must be pasteurized. Most US feta is pasteurized - check label.", alternatives: "Pasteurized feta, goat cheese", source: "FDA", learnMoreUrl: "https://www.fda.gov/food/people-risk-foodborne-illness/listeria-and-pregnancy" },
    "blue cheese": { item: "Blue Cheese", status: "Caution", reason: "Safe if pasteurized. Avoid if made with raw milk.", alternatives: "Pasteurized blue cheese, or other strong cheeses" },
    "cream cheese": { item: "Cream Cheese", status: "Safe", reason: "Commercial cream cheese is pasteurized and safe.", alternatives: "" },
    "cottage cheese": { item: "Cottage Cheese", status: "Safe", reason: "Pasteurized and safe. Great protein source.", alternatives: "" },
    "cheddar": { item: "Cheddar", status: "Safe", reason: "Hard cheeses are safe even if unpasteurized (aging kills bacteria).", alternatives: "" },
    "parmesan": { item: "Parmesan", status: "Safe", reason: "Aged hard cheese - very safe.", alternatives: "" },
    "mozzarella": { item: "Mozzarella", status: "Safe", reason: "Fresh mozzarella is safe if pasteurized (most are). Check label.", alternatives: "" },
    "milk": { item: "Milk", status: "Safe", reason: "Pasteurized milk is completely safe. Avoid raw/unpasteurized milk.", alternatives: "", source: "FDA", learnMoreUrl: "https://www.fda.gov/food/buy-store-serve-safe-food/food-safety-moms-be" },
    "yogurt": { item: "Yogurt", status: "Safe", reason: "Safe and beneficial - contains probiotics that support digestion.", alternatives: "" },
    "ice cream": { item: "Ice Cream", status: "Safe", reason: "Commercial ice cream uses pasteurized ingredients. Avoid soft-serve from machines that may not be cleaned well.", alternatives: "Pre-packaged ice cream" },
    "eggs": { item: "Eggs", status: "Safe", reason: "Safe when fully cooked (firm whites and yolks). Avoid runny yolks.", alternatives: "", source: "FDA", learnMoreUrl: "https://www.fda.gov/food/people-risk-foodborne-illness/food-safety-moms-be" },
    "runny eggs": { item: "Runny/Soft Boiled Eggs", status: "Avoid", reason: "Undercooked eggs risk Salmonella. Cook until yolk is firm.", alternatives: "Hard-boiled eggs, scrambled eggs, fully cooked omelets", source: "FDA", learnMoreUrl: "https://www.fda.gov/food/people-risk-foodborne-illness/food-safety-moms-be" },
    "mayonnaise": { item: "Mayonnaise (Commercial)", status: "Safe", reason: "Commercial mayo uses pasteurized eggs and is safe.", alternatives: "" },
    "hollandaise": { item: "Hollandaise Sauce", status: "Caution", reason: "Often made with undercooked eggs. Restaurant hollandaise may be risky.", alternatives: "Ask if pasteurized eggs are used, or skip" },
    "cookie dough": { item: "Raw Cookie Dough", status: "Avoid", reason: "Contains raw eggs and raw flour (both risky). Don't lick the spoon!", alternatives: "Edible cookie dough (made for eating raw)" },
    "cake batter": { item: "Raw Cake Batter", status: "Avoid", reason: "Raw eggs and raw flour pose risks.", alternatives: "Baked treats only" },

    // BEVERAGES
    "coffee": { item: "Coffee", status: "Safe", reason: "Safe in moderation. Limit to 200mg caffeine/day (about one 12oz cup).", alternatives: "Decaf coffee, half-caf", source: "ACOG", learnMoreUrl: "https://www.acog.org/womens-health/faqs/nutrition-during-pregnancy" },
    "caffeine": { item: "Caffeine", status: "Caution", reason: "Limit to 200mg per day total. This includes coffee, tea, soda, and chocolate.", alternatives: "Decaf options, herbal tea", source: "ACOG", learnMoreUrl: "https://www.acog.org/womens-health/faqs/nutrition-during-pregnancy" },
    "tea": { item: "Tea (Black/Green)", status: "Safe", reason: "Safe in moderation - contains caffeine. Count toward 200mg daily limit.", alternatives: "Herbal teas (most are safe)" },
    "herbal tea": { item: "Herbal Tea", status: "Safe", reason: "Most are safe: ginger, peppermint, chamomile, lemon balm. Avoid large amounts of licorice or hibiscus.", alternatives: "" },
    "green tea": { item: "Green Tea", status: "Safe", reason: "Contains less caffeine than coffee. 2-3 cups daily is fine.", alternatives: "" },
    "alcohol": { item: "Alcohol", status: "Avoid", reason: "No known safe amount during pregnancy. Risks include Fetal Alcohol Spectrum Disorders.", alternatives: "Mocktails, sparkling water with lime, non-alcoholic beer/wine", source: "CDC", learnMoreUrl: "https://www.cdc.gov/fasd/alcohol-use.html" },
    "wine": { item: "Wine", status: "Avoid", reason: "No safe amount established. Best to avoid entirely.", alternatives: "Sparkling grape juice, dealcoholized wine", source: "CDC", learnMoreUrl: "https://www.cdc.gov/fasd/alcohol-use.html" },
    "beer": { item: "Beer", status: "Avoid", reason: "No safe amount of alcohol during pregnancy.", alternatives: "Non-alcoholic beer (check label - some still have small amounts)", source: "CDC", learnMoreUrl: "https://www.cdc.gov/fasd/alcohol-use.html" },
    "soda": { item: "Soda", status: "Safe", reason: "Safe in moderation. Watch sugar intake and count caffeine (cola has ~35mg per can).", alternatives: "Sparkling water, flavored seltzer" },
    "energy drinks": { item: "Energy Drinks", status: "Avoid", reason: "Often contain 150-300mg caffeine per can (over the daily limit), plus other stimulants.", alternatives: "Water, light coffee, natural energy snacks", source: "ACOG", learnMoreUrl: "https://www.acog.org/womens-health/faqs/nutrition-during-pregnancy" },
    "kombucha": { item: "Kombucha", status: "Caution", reason: "Unpasteurized kombucha has bacteria and trace alcohol. Pasteurized versions are safer.", alternatives: "Pasteurized kombucha, sparkling water" },

    // PRODUCE & OTHER
    "sprouts": { item: "Raw Sprouts", status: "Avoid", reason: "Raw sprouts (alfalfa, mung bean, clover) can harbor Salmonella and E. coli.", alternatives: "Cooked sprouts, lettuce, other greens", source: "FDA", learnMoreUrl: "https://www.fda.gov/food/people-risk-foodborne-illness/food-safety-moms-be" },
    "salad": { item: "Salad", status: "Caution", reason: "Wash all produce thoroughly. Pre-washed bagged salads are generally safe.", alternatives: "Home-washed greens" },
    "unwashed produce": { item: "Unwashed Produce", status: "Avoid", reason: "Always wash fruits and vegetables to remove bacteria and pesticides.", alternatives: "Thoroughly washed produce" },
    "papaya": { item: "Unripe Papaya", status: "Avoid", reason: "Unripe (green) papaya contains latex which can trigger contractions. Ripe papaya is fine.", alternatives: "Ripe papaya, other tropical fruits" },
    "pineapple": { item: "Pineapple", status: "Safe", reason: "Safe in normal amounts. The 'induces labor' myth is false - you'd need impossible amounts.", alternatives: "" },
    "honey": { item: "Honey", status: "Safe", reason: "Safe for pregnant women (only risky for babies under 1 year due to infant botulism).", alternatives: "" },
    "artificial sweeteners": { item: "Artificial Sweeteners", status: "Safe", reason: "Aspartame, sucralose, and stevia are considered safe in moderation.", alternatives: "" },
    "soy": { item: "Soy Products", status: "Safe", reason: "Tofu, edamame, soy milk are all safe and nutritious.", alternatives: "" },
    "peanuts": { item: "Peanuts", status: "Safe", reason: "Safe unless you have an allergy. Eating peanuts during pregnancy doesn't cause baby allergies.", alternatives: "" },
    "spicy food": { item: "Spicy Food", status: "Safe", reason: "Won't harm baby. May cause heartburn for you though!", alternatives: "" },
    "leftovers": { item: "Leftovers", status: "Caution", reason: "Reheat to steaming hot (165°F). Don't eat cold leftovers. Use within 3-4 days.", alternatives: "Freshly cooked meals" },
    "buffet food": { item: "Buffet / Potluck Food", status: "Caution", reason: "Food sitting at room temperature breeds bacteria. Only eat if freshly set out and hot.", alternatives: "Fresh-cooked options" },

    // MORE FISH & SEAFOOD
    "halibut": { item: "Halibut", status: "Safe", reason: "Low to moderate mercury, safe in moderation (6oz per week).", alternatives: "" },
    "trout": { item: "Trout", status: "Safe", reason: "Low in mercury, excellent omega-3 source.", alternatives: "" },
    "pollock": { item: "Pollock", status: "Safe", reason: "Very low mercury. Often used in fish sticks and fast food.", alternatives: "" },
    "sole": { item: "Sole", status: "Safe", reason: "Low mercury flatfish. Safe choice.", alternatives: "" },
    "flounder": { item: "Flounder", status: "Safe", reason: "Low mercury, mild taste.", alternatives: "" },
    "sea bass": { item: "Sea Bass (Chilean)", status: "Caution", reason: "Moderate to high mercury. Limit to 6oz per week.", alternatives: "Salmon, tilapia" },
    "grouper": { item: "Grouper", status: "Caution", reason: "Moderate mercury levels. Limit intake.", alternatives: "Lower mercury fish" },
    "mahi mahi": { item: "Mahi Mahi", status: "Safe", reason: "Lower mercury than many large fish. Safe in moderation.", alternatives: "" },
    "scallops": { item: "Scallops", status: "Safe", reason: "Low mercury, safe when fully cooked.", alternatives: "" },
    "mussels": { item: "Mussels", status: "Safe", reason: "Low mercury. Must be cooked until shells open.", alternatives: "" },
    "clams": { item: "Clams", status: "Safe", reason: "Low mercury. Cook until shells open.", alternatives: "" },
    "king crab": { item: "King Crab", status: "Safe", reason: "Low mercury, high protein.", alternatives: "" },
    "crawfish": { item: "Crawfish / Crayfish", status: "Safe", reason: "Low mercury freshwater shellfish. Cook thoroughly.", alternatives: "" },
    "eel": { item: "Eel (Unagi)", status: "Safe", reason: "Safe when cooked (as in sushi restaurants). Avoid raw.", alternatives: "" },
    "marlin": { item: "Marlin", status: "Avoid", reason: "Very high in mercury. Skip during pregnancy.", alternatives: "Salmon, mahi mahi" },
    "tilefish": { item: "Tilefish", status: "Avoid", reason: "Extremely high mercury levels. Avoid completely.", alternatives: "Any low-mercury fish" },
    "orange roughy": { item: "Orange Roughy", status: "Avoid", reason: "High mercury due to long lifespan. Avoid.", alternatives: "Tilapia, cod" },
    "bigeye tuna": { item: "Bigeye Tuna", status: "Avoid", reason: "Very high mercury. Avoid entirely during pregnancy.", alternatives: "Light canned tuna, salmon" },
    "ceviche": { item: "Ceviche", status: "Avoid", reason: "Citrus doesn't kill parasites like cooking does. Avoid raw fish preparations.", alternatives: "Cooked shrimp cocktail" },
    "fish sauce": { item: "Fish Sauce", status: "Safe", reason: "Fermented and salty, safe in cooking amounts.", alternatives: "" },
    "fish oil supplements": { item: "Fish Oil Supplements", status: "Safe", reason: "Purified supplements are safe. Choose 'prenatal' versions to ensure low contaminants.", alternatives: "" },

    // MORE MEAT
    "lamb": { item: "Lamb", status: "Safe", reason: "Safe when cooked to 145°F for whole cuts, 160°F for ground.", alternatives: "" },
    "duck": { item: "Duck", status: "Safe", reason: "Safe when fully cooked. Duck can be slightly pink but should reach 165°F.", alternatives: "" },
    "venison": { item: "Venison (Deer)", status: "Caution", reason: "Safe when fully cooked. Avoid if the deer was potentially exposed to lead shot.", alternatives: "Farm-raised venison" },
    "bison": { item: "Bison / Buffalo", status: "Safe", reason: "Lean red meat, safe when cooked to 160°F for ground.", alternatives: "" },
    "rabbit": { item: "Rabbit", status: "Safe", reason: "Safe when thoroughly cooked.", alternatives: "" },
    "goat": { item: "Goat", status: "Safe", reason: "Safe when fully cooked. Popular in many cuisines.", alternatives: "" },
    "veal": { item: "Veal", status: "Safe", reason: "Safe when cooked to proper temperature (145°F minimum).", alternatives: "" },
    "prosciutto": { item: "Prosciutto", status: "Caution", reason: "Dry-cured, not cooked. Heat until steaming if concerned about Listeria.", alternatives: "Cooked ham" },
    "chorizo": { item: "Chorizo (Cooked)", status: "Safe", reason: "Mexican chorizo (raw) must be fully cooked. Spanish chorizo (cured) - heat to be safe.", alternatives: "" },
    "meatballs": { item: "Meatballs", status: "Safe", reason: "Safe when cooked to 160°F internal temperature.", alternatives: "" },
    "organ meats": { item: "Organ Meats (except liver)", status: "Caution", reason: "Kidney, heart, etc. are safe in moderation. Avoid liver (too much Vitamin A).", alternatives: "Lean muscle meats" },
    "bone broth": { item: "Bone Broth", status: "Safe", reason: "Nutritious and safe. Great source of collagen.", alternatives: "" },

    // MORE DAIRY & EGGS
    "goat cheese": { item: "Goat Cheese (Chèvre)", status: "Caution", reason: "Safe if pasteurized. Check label - many are pasteurized.", alternatives: "Pasteurized goat cheese" },
    "ricotta": { item: "Ricotta", status: "Safe", reason: "Usually pasteurized. Check label to confirm.", alternatives: "" },
    "queso fresco": { item: "Queso Fresco", status: "Caution", reason: "Sometimes made with unpasteurized milk. Check label carefully or avoid.", alternatives: "Pasteurized Mexican cheeses" },
    "paneer": { item: "Paneer", status: "Safe", reason: "Usually made from pasteurized milk and cooked in dishes.", alternatives: "" },
    "halloumi": { item: "Halloumi", status: "Safe", reason: "Usually pasteurized. Best when grilled/cooked.", alternatives: "" },
    "kefir": { item: "Kefir", status: "Safe", reason: "Pasteurized kefir is safe and probiotic-rich.", alternatives: "" },
    "whipped cream": { item: "Whipped Cream", status: "Safe", reason: "Commercial whipped cream is pasteurized. Fresh whipped from pasteurized cream is also fine.", alternatives: "" },
    "custard": { item: "Custard", status: "Caution", reason: "Homemade may have undercooked eggs. Commercial/store-bought is usually safe.", alternatives: "Store-bought custard" },
    "tiramisu": { item: "Tiramisu", status: "Caution", reason: "Contains raw eggs and sometimes alcohol. Commercial versions may use pasteurized eggs.", alternatives: "Pregnancy-safe tiramisu recipes" },
    "meringue": { item: "Meringue", status: "Caution", reason: "Raw meringue uses uncooked eggs. Baked meringue is safe.", alternatives: "Baked meringue desserts" },
    "eggnog": { item: "Eggnog (Homemade)", status: "Avoid", reason: "Traditional eggnog has raw eggs. Commercial pasteurized eggnog is safe.", alternatives: "Store-bought pasteurized eggnog" },
    "deviled eggs": { item: "Deviled Eggs", status: "Safe", reason: "Made from hard-boiled eggs - completely safe.", alternatives: "" },
    "quiche": { item: "Quiche", status: "Safe", reason: "Eggs are cooked in the baking process.", alternatives: "" },

    // GRAINS & CARBS
    "bread": { item: "Bread", status: "Safe", reason: "All bread is safe. Good source of carbs and some fiber.", alternatives: "" },
    "pasta": { item: "Pasta", status: "Safe", reason: "Safe when cooked. Whole grain is more nutritious.", alternatives: "" },
    "rice": { item: "Rice", status: "Safe", reason: "Safe. Arsenic concerns exist but moderate consumption is fine.", alternatives: "" },
    "quinoa": { item: "Quinoa", status: "Safe", reason: "Excellent protein source, safe and nutritious.", alternatives: "" },
    "oatmeal": { item: "Oatmeal", status: "Safe", reason: "Great for fiber and iron. Very pregnancy-friendly.", alternatives: "" },
    "granola": { item: "Granola", status: "Safe", reason: "Safe. Watch sugar content in commercial brands.", alternatives: "" },
    "cereal": { item: "Cereal", status: "Safe", reason: "Safe. Fortified cereals provide folic acid and iron.", alternatives: "" },
    "flour": { item: "Raw Flour", status: "Avoid", reason: "Raw flour can contain E. coli. Don't eat raw dough or batter.", alternatives: "Baked goods only" },

    // FRUITS & VEGETABLES
    "apple": { item: "Apples", status: "Safe", reason: "Safe and nutritious. Wash well before eating.", alternatives: "" },
    "banana": { item: "Bananas", status: "Safe", reason: "Great source of potassium and easy on the stomach.", alternatives: "" },
    "berries": { item: "Berries", status: "Safe", reason: "Excellent antioxidants. Wash thoroughly.", alternatives: "" },
    "grapes": { item: "Grapes", status: "Safe", reason: "Safe in moderation. Wash well.", alternatives: "" },
    "mango": { item: "Mango (Ripe)", status: "Safe", reason: "Safe and delicious. High in vitamins A and C.", alternatives: "" },
    "watermelon": { item: "Watermelon", status: "Safe", reason: "Hydrating and safe. Great for pregnancy hydration.", alternatives: "" },
    "avocado": { item: "Avocado", status: "Safe", reason: "Excellent source of healthy fats and folate!", alternatives: "" },
    "spinach": { item: "Spinach", status: "Safe", reason: "Excellent iron and folate source. Wash well.", alternatives: "" },
    "kale": { item: "Kale", status: "Safe", reason: "Nutrient-dense superfood. Safe when washed.", alternatives: "" },
    "broccoli": { item: "Broccoli", status: "Safe", reason: "Great source of fiber, vitamin C, and folate.", alternatives: "" },
    "carrots": { item: "Carrots", status: "Safe", reason: "Safe and high in vitamin A (beta-carotene form is safe).", alternatives: "" },
    "tomatoes": { item: "Tomatoes", status: "Safe", reason: "Safe fresh or cooked. May worsen heartburn.", alternatives: "" },
    "cucumber": { item: "Cucumbers", status: "Safe", reason: "Hydrating and safe. Wash well.", alternatives: "" },
    "celery": { item: "Celery", status: "Safe", reason: "Safe. Can help with water retention.", alternatives: "" },
    "mushrooms": { item: "Mushrooms (Cooked)", status: "Safe", reason: "Safe when cooked. Avoid eating raw mushrooms.", alternatives: "" },
    "asparagus": { item: "Asparagus", status: "Safe", reason: "Good source of folate! Very pregnancy-friendly.", alternatives: "" },
    "beets": { item: "Beets", status: "Safe", reason: "Safe and nutritious. May help with blood pressure.", alternatives: "" },
    "sweet potato": { item: "Sweet Potatoes", status: "Safe", reason: "Excellent vitamin A source (safe form). Very nutritious.", alternatives: "" },

    // INTERNATIONAL FOODS
    "sashimi": { item: "Sashimi", status: "Avoid", reason: "Raw fish - avoid during pregnancy.", alternatives: "Cooked sushi rolls" },
    "pho": { item: "Pho", status: "Safe", reason: "Hot soup is safe. Add extra veggies for nutrition.", alternatives: "" },
    "pad thai": { item: "Pad Thai", status: "Safe", reason: "Safe when fully cooked. Watch sodium content.", alternatives: "" },
    "curry": { item: "Curry", status: "Safe", reason: "Spices are safe. May cause heartburn but won't harm baby.", alternatives: "" },
    "hummus": { item: "Hummus", status: "Safe", reason: "Pasteurized commercial hummus is safe. Great protein snack.", alternatives: "" },
    "falafel": { item: "Falafel", status: "Safe", reason: "Fried/baked chickpeas - safe and nutritious.", alternatives: "" },
    "kimchi": { item: "Kimchi", status: "Safe", reason: "Fermented, probiotic-rich. Safe in moderation.", alternatives: "" },
    "miso": { item: "Miso Soup", status: "Safe", reason: "Fermented soybean paste is safe. Good probiotics.", alternatives: "" },
    "tempeh": { item: "Tempeh", status: "Safe", reason: "Fermented soy product. Cook before eating.", alternatives: "" },
    "natto": { item: "Natto", status: "Safe", reason: "Fermented soybeans. Safe but acquired taste.", alternatives: "" },
    "tacos": { item: "Tacos", status: "Safe", reason: "Safe when meat is fully cooked.", alternatives: "" },
    "sauerkraut": { item: "Sauerkraut", status: "Safe", reason: "Fermented cabbage. Safe and probiotic-rich.", alternatives: "" },
    "pickles": { item: "Pickles", status: "Safe", reason: "Safe! A classic pregnancy craving.", alternatives: "" },

    // DESSERTS & SWEETS
    "chocolate": { item: "Chocolate", status: "Safe", reason: "Safe in moderation. Dark chocolate has less sugar. Count caffeine (1oz = ~20mg).", alternatives: "" },
    "brownies": { item: "Brownies", status: "Safe", reason: "Safe when baked. Avoid eating the raw batter.", alternatives: "" },
    "cookies": { item: "Cookies (Baked)", status: "Safe", reason: "Baked cookies are safe. No raw dough!", alternatives: "" },
    "cheesecake": { item: "Cheesecake", status: "Safe", reason: "Commercial cheesecakes use pasteurized ingredients. Safe.", alternatives: "" },
    "mousse": { item: "Chocolate Mousse", status: "Caution", reason: "Often contains raw eggs. Ask if pasteurized eggs are used.", alternatives: "Cooked chocolate pudding" },
    "souffle": { item: "Soufflé", status: "Safe", reason: "Baked soufflés cook the eggs. Safe to eat.", alternatives: "" },
    "creme brulee": { item: "Crème Brûlée", status: "Safe", reason: "The custard is cooked. Safe when prepared properly.", alternatives: "" },
    "gelato": { item: "Gelato", status: "Safe", reason: "Commercial gelato is pasteurized. Safe!", alternatives: "" },
    "frozen yogurt": { item: "Frozen Yogurt", status: "Safe", reason: "Pasteurized, safe. Avoid self-serve machines if hygiene is a concern.", alternatives: "" },
    "popsicles": { item: "Popsicles", status: "Safe", reason: "Safe! Great for soothing pregnancy nausea.", alternatives: "" },
    "candy": { item: "Candy", status: "Safe", reason: "Safe in moderation. Watch sugar intake.", alternatives: "" },
    "licorice": { item: "Licorice (Black)", status: "Caution", reason: "Large amounts of real licorice may cause issues. Occasional small amounts are fine.", alternatives: "Red licorice (not real licorice)" },

    // NUTS & SEEDS
    "almonds": { item: "Almonds", status: "Safe", reason: "Excellent snack! High in protein and healthy fats.", alternatives: "" },
    "walnuts": { item: "Walnuts", status: "Safe", reason: "Great source of omega-3s. Healthy for baby's brain.", alternatives: "" },
    "cashews": { item: "Cashews", status: "Safe", reason: "Nutritious and safe. Good protein source.", alternatives: "" },
    "pistachios": { item: "Pistachios", status: "Safe", reason: "Safe and delicious. Good fiber source.", alternatives: "" },
    "macadamia": { item: "Macadamia Nuts", status: "Safe", reason: "Safe. High in healthy fats.", alternatives: "" },
    "pine nuts": { item: "Pine Nuts", status: "Safe", reason: "Safe. Great in pesto and salads.", alternatives: "" },
    "sunflower seeds": { item: "Sunflower Seeds", status: "Safe", reason: "Safe and nutritious. Good folate source.", alternatives: "" },
    "pumpkin seeds": { item: "Pumpkin Seeds", status: "Safe", reason: "Excellent! High in iron and zinc.", alternatives: "" },
    "chia seeds": { item: "Chia Seeds", status: "Safe", reason: "Superfood! High in omega-3s and fiber.", alternatives: "" },
    "flax seeds": { item: "Flax Seeds", status: "Safe", reason: "Safe in moderation. Good omega-3 source.", alternatives: "" },
    "hemp seeds": { item: "Hemp Seeds", status: "Safe", reason: "Nutritious and safe. Complete protein.", alternatives: "" },

    // CONDIMENTS & SAUCES
    "ketchup": { item: "Ketchup", status: "Safe", reason: "Safe. Commercial ketchup is pasteurized.", alternatives: "" },
    "mustard": { item: "Mustard", status: "Safe", reason: "Safe in normal amounts.", alternatives: "" },
    "hot sauce": { item: "Hot Sauce", status: "Safe", reason: "Safe! Spice won't harm baby.", alternatives: "" },
    "soy sauce": { item: "Soy Sauce", status: "Safe", reason: "Safe in moderation. High sodium - watch intake.", alternatives: "Low-sodium soy sauce" },
    "teriyaki": { item: "Teriyaki Sauce", status: "Safe", reason: "Safe. Watch sodium content.", alternatives: "" },
    "salsa": { item: "Salsa", status: "Safe", reason: "Safe! Tomatoes and peppers are nutritious.", alternatives: "" },
    "guacamole": { item: "Guacamole", status: "Safe", reason: "Safe and nutritious. Avocados are great for pregnancy.", alternatives: "" },
    "ranch": { item: "Ranch Dressing", status: "Safe", reason: "Commercial ranch is safe. Made with pasteurized ingredients.", alternatives: "" },
    "caesar dressing": { item: "Caesar Dressing", status: "Caution", reason: "Traditional recipes use raw eggs. Check if pasteurized.", alternatives: "Commercial/pasteurized Caesar dressing" },
    "vinaigrette": { item: "Vinaigrette", status: "Safe", reason: "Oil and vinegar dressings are safe.", alternatives: "" },
    "balsamic": { item: "Balsamic Vinegar", status: "Safe", reason: "Safe and delicious.", alternatives: "" },
    "tahini": { item: "Tahini", status: "Safe", reason: "Safe. Made from sesame seeds.", alternatives: "" },
    "pesto": { item: "Pesto", status: "Safe", reason: "Safe when commercial or freshly made with pasteurized cheese.", alternatives: "" },
    "bbq sauce": { item: "BBQ Sauce", status: "Safe", reason: "Safe. Commercial BBQ sauce is cooked.", alternatives: "" },
    "worcestershire": { item: "Worcestershire Sauce", status: "Safe", reason: "Safe in cooking amounts. Contains anchovies.", alternatives: "" },

    // SNACKS
    "chips": { item: "Potato Chips", status: "Safe", reason: "Safe in moderation. Watch sodium intake.", alternatives: "" },
    "pretzels": { item: "Pretzels", status: "Safe", reason: "Safe. Good for nausea!", alternatives: "" },
    "popcorn": { item: "Popcorn", status: "Safe", reason: "Safe! Whole grain and fiber-rich. Go easy on butter.", alternatives: "" },
    "crackers": { item: "Crackers", status: "Safe", reason: "Safe. Great for morning sickness.", alternatives: "" },
    "trail mix": { item: "Trail Mix", status: "Safe", reason: "Safe and nutritious. Good for snacking.", alternatives: "" },
    "protein bar": { item: "Protein Bars", status: "Safe", reason: "Generally safe. Check ingredients for any concerns.", alternatives: "" },
    "rice cakes": { item: "Rice Cakes", status: "Safe", reason: "Safe. Low calorie snack option.", alternatives: "" },

    // MORE INTERNATIONAL FOODS
    "ramen": { item: "Ramen (Instant)", status: "Safe", reason: "Safe but high in sodium. Add veggies and protein!", alternatives: "" },
    "spring rolls": { item: "Spring Rolls (Cooked)", status: "Safe", reason: "Fried or baked spring rolls are safe.", alternatives: "" },
    "fresh spring rolls": { item: "Fresh Spring Rolls (Raw)", status: "Caution", reason: "Ensure vegetables are washed. Avoid if raw meat/fish inside.", alternatives: "Cooked spring rolls" },
    "bibimbap": { item: "Bibimbap", status: "Safe", reason: "Safe when egg is fully cooked and meat is cooked through.", alternatives: "" },
    "bulgogi": { item: "Bulgogi", status: "Safe", reason: "Korean BBQ is safe when meat is fully cooked.", alternatives: "" },
    "dumplings": { item: "Dumplings", status: "Safe", reason: "Safe when fully cooked (steamed, boiled, or fried).", alternatives: "" },
    "dim sum": { item: "Dim Sum", status: "Safe", reason: "Safe when freshly steamed and hot.", alternatives: "" },
    "fried rice": { item: "Fried Rice", status: "Safe", reason: "Safe when freshly made and eggs are cooked.", alternatives: "" },
    "lo mein": { item: "Lo Mein", status: "Safe", reason: "Safe. Noodles and veggies cooked together.", alternatives: "" },
    "chow mein": { item: "Chow Mein", status: "Safe", reason: "Safe when freshly prepared.", alternatives: "" },
    "orange chicken": { item: "Orange Chicken", status: "Safe", reason: "Safe. Chicken is fully cooked.", alternatives: "" },
    "kung pao": { item: "Kung Pao Chicken", status: "Safe", reason: "Safe. Fully cooked dish.", alternatives: "" },
    "tikka masala": { item: "Tikka Masala", status: "Safe", reason: "Safe. Spices are fine for baby.", alternatives: "" },
    "biryani": { item: "Biryani", status: "Safe", reason: "Safe. Rice and meat cooked thoroughly.", alternatives: "" },
    "naan": { item: "Naan", status: "Safe", reason: "Safe. Baked bread.", alternatives: "" },
    "samosa": { item: "Samosa", status: "Safe", reason: "Safe when freshly fried.", alternatives: "" },
    "shawarma": { item: "Shawarma", status: "Safe", reason: "Safe when meat is freshly cooked and hot.", alternatives: "" },
    "fajitas": { item: "Fajitas", status: "Safe", reason: "Safe when meat is fully cooked.", alternatives: "" },
    "enchiladas": { item: "Enchiladas", status: "Safe", reason: "Safe. Cooked filling and melted cheese.", alternatives: "" },
    "quesadilla": { item: "Quesadilla", status: "Safe", reason: "Safe. Cheese melts and filling is heated.", alternatives: "" },
    "nachos": { item: "Nachos", status: "Safe", reason: "Safe when cheese is melted and toppings are fresh.", alternatives: "" },
    "gyros": { item: "Gyros", status: "Safe", reason: "Safe when meat is freshly cooked and hot.", alternatives: "" },
    "souvlaki": { item: "Souvlaki", status: "Safe", reason: "Safe. Grilled meat on skewers.", alternatives: "" },
    "moussaka": { item: "Moussaka", status: "Safe", reason: "Safe. Baked casserole dish.", alternatives: "" },
    "pierogi": { item: "Pierogi", status: "Safe", reason: "Safe. Boiled or fried dumplings.", alternatives: "" },
    "borscht": { item: "Borscht", status: "Safe", reason: "Safe. Hot beet soup.", alternatives: "" },
    "risotto": { item: "Risotto", status: "Safe", reason: "Safe. Cooked rice dish.", alternatives: "" },
    "gnocchi": { item: "Gnocchi", status: "Safe", reason: "Safe. Potato dumplings.", alternatives: "" },
    "bruschetta": { item: "Bruschetta", status: "Safe", reason: "Safe. Toasted bread with toppings.", alternatives: "" },
    "caprese": { item: "Caprese Salad", status: "Safe", reason: "Safe if mozzarella is pasteurized (most is).", alternatives: "" },
    "gazpacho": { item: "Gazpacho", status: "Safe", reason: "Safe cold soup. Wash vegetables well.", alternatives: "" },
    "paella": { item: "Paella", status: "Safe", reason: "Safe when seafood/meat is fully cooked.", alternatives: "" },
    "baklava": { item: "Baklava", status: "Safe", reason: "Safe. Baked pastry with nuts and honey.", alternatives: "" },
};

// --- FLASH FACTS (50+ pregnancy facts) ---
export const FLASH_FACTS_REPO: FlashFact[] = [
    // Baby Development
    { id: '1', category: 'baby', fact: 'Your baby can taste the food you eat through amniotic fluid starting around week 21.', emoji: '👅' },
    { id: '2', category: 'baby', fact: 'Babies can hear sounds from the outside world starting around week 18-20.', emoji: '👂' },
    { id: '3', category: 'baby', fact: 'Fingerprints form around week 13 and are completely unique.', emoji: '👆' },
    { id: '4', category: 'baby', fact: 'Babies open their eyes and can see light through the belly around week 26-28.', emoji: '👀' },
    { id: '5', category: 'baby', fact: 'Your baby practices crying in the womb - they make crying faces and motions.', emoji: '😢' },
    { id: '6', category: 'baby', fact: 'By week 32, babies dream! They have REM sleep cycles.', emoji: '💭' },
    { id: '7', category: 'baby', fact: 'Babies can hiccup in the womb starting around week 9. You may feel it later!', emoji: '😮' },
    { id: '8', category: 'baby', fact: 'Your baby is covered in fine hair called lanugo that usually sheds before birth.', emoji: '🧸' },
    { id: '9', category: 'baby', fact: 'Babies practice breathing movements in the womb using amniotic fluid.', emoji: '🌬️' },
    { id: '10', category: 'baby', fact: 'A baby\'s heart starts beating around day 22 after conception.', emoji: '💓' },
    { id: '11', category: 'baby', fact: 'At 12 weeks, your baby is about 2 inches long but fully formed.', emoji: '📏' },
    { id: '12', category: 'baby', fact: 'Babies develop taste buds around week 8 and prefer sweet flavors.', emoji: '🍬' },
    { id: '13', category: 'baby', fact: 'Your baby can recognize your voice and will turn toward it after birth.', emoji: '🗣️' },
    { id: '14', category: 'baby', fact: 'Babies yawn in the womb, possibly to help develop the brain.', emoji: '🥱' },
    { id: '15', category: 'baby', fact: 'The amniotic sac can hold up to 1 liter of fluid at its peak.', emoji: '💧' },

    // Mom's Body
    { id: '16', category: 'mom', fact: 'Blood volume increases by about 50% during pregnancy.', emoji: '🩸' },
    { id: '17', category: 'mom', fact: 'Your heart actually grows slightly larger to pump extra blood.', emoji: '❤️' },
    { id: '18', category: 'mom', fact: 'The uterus expands to 500 times its original size during pregnancy.', emoji: '🎈' },
    { id: '19', category: 'mom', fact: 'Relaxin hormone loosens joints, which is why feet often grow a half size.', emoji: '🦶' },
    { id: '20', category: 'mom', fact: 'Pregnancy brain is real! Hormones temporarily affect memory and focus.', emoji: '🧠' },
    { id: '21', category: 'mom', fact: 'Your sense of smell becomes super-powered due to hormones.', emoji: '👃' },
    { id: '22', category: 'mom', fact: 'Hair often becomes thicker during pregnancy because you shed less.', emoji: '💇' },
    { id: '23', category: 'mom', fact: 'The placenta is the only organ humans grow temporarily and then discard.', emoji: '🫀' },
    { id: '24', category: 'mom', fact: 'Babies can leave DNA in the mother\'s body that stays for years (microchimerism).', emoji: '🧬' },
    { id: '25', category: 'mom', fact: 'During pregnancy, all your organs shift to make room for baby.', emoji: '🫁' },
    { id: '26', category: 'mom', fact: 'Morning sickness may be caused by baby protecting itself from toxins.', emoji: '🤢' },
    { id: '27', category: 'mom', fact: 'Your body produces a hormone called relaxin that helps your pelvis widen for birth.', emoji: '🦴' },
    { id: '28', category: 'mom', fact: 'Pregnant women produce more estrogen in one day than non-pregnant women do in 3 years.', emoji: '📈' },
    { id: '29', category: 'mom', fact: 'The dark line on your belly (linea nigra) fades after pregnancy.', emoji: '➖' },
    { id: '30', category: 'mom', fact: 'Contractions can continue for a while after birth as the uterus shrinks back.', emoji: '💪' },

    // Partner Facts
    { id: '31', category: 'partner', fact: 'Couvade syndrome: Up to 90% of partners experience sympathy pregnancy symptoms.', emoji: '🤝' },
    { id: '32', category: 'partner', fact: 'Partner testosterone drops during pregnancy, preparing them for caregiving.', emoji: '📉' },
    { id: '33', category: 'partner', fact: 'Skin-to-skin contact with baby after birth benefits partners too, not just mom.', emoji: '🤱' },
    { id: '34', category: 'partner', fact: 'Partners who attend prenatal appointments report feeling more connected.', emoji: '📅' },
    { id: '35', category: 'partner', fact: 'Talking to baby through the belly helps them recognize your voice after birth.', emoji: '💬' },
    { id: '36', category: 'partner', fact: 'Partners can help reduce labor pain through massage and counter-pressure.', emoji: '💆' },

    // Science & History
    { id: '37', category: 'science', fact: 'Babies are born with about 300 bones, which fuse into 206 as they grow.', emoji: '🦴' },
    { id: '38', category: 'science', fact: 'Twins can have different fathers in rare cases (superfecundation).', emoji: '👯' },
    { id: '39', category: 'science', fact: 'The longest recorded pregnancy was 375 days (about 12.5 months).', emoji: '📅' },
    { id: '40', category: 'science', fact: 'Babies are born with natural swimming reflexes that disappear after a few months.', emoji: '🏊' },
    { id: '41', category: 'science', fact: 'Only about 5% of babies are born on their actual due date.', emoji: '🎯' },
    { id: '42', category: 'science', fact: 'The odds of having identical twins are about 1 in 250.', emoji: '👶👶' },
    { id: '43', category: 'science', fact: 'First-time moms tend to carry about 5 days past their due date on average.', emoji: '⏰' },
    { id: '44', category: 'science', fact: 'Baby girls are born with all the eggs they\'ll ever have (about 1-2 million).', emoji: '🥚' },
    { id: '45', category: 'science', fact: 'Ultrasounds were first used for pregnancy in 1958.', emoji: '📷' },
    { id: '46', category: 'science', fact: 'The average newborn sleeps 16-17 hours a day.', emoji: '😴' },
    { id: '47', category: 'science', fact: 'Babies recognize their mother\'s voice immediately after birth.', emoji: '🎵' },
    { id: '48', category: 'science', fact: 'A baby\'s head is 1/4 of their total body length at birth.', emoji: '👤' },
    { id: '49', category: 'science', fact: 'Newborns can only see about 8-12 inches clearly - perfect for seeing mom while nursing.', emoji: '👁️' },
    { id: '50', category: 'science', fact: 'The due date is calculated from the first day of the last period, not conception.', emoji: '🗓️' },
];

// --- HOSPITAL BAG CHECKLIST ---
const HOSPITAL_BAG_ITEMS: ChecklistItem[] = [
    // Documents
    { id: 'doc-1', text: "Photo ID (Driver's License)", category: 'docs', isChecked: false, addedBy: 'ai' },
    { id: 'doc-2', text: "Insurance Card", category: 'docs', isChecked: false, addedBy: 'ai' },
    { id: 'doc-3', text: "Hospital Pre-Registration Paperwork", category: 'docs', isChecked: false, addedBy: 'ai' },
    { id: 'doc-4', text: "Birth Plan (3 copies)", category: 'docs', isChecked: false, addedBy: 'ai' },
    { id: 'doc-5', text: "List of Emergency Contacts", category: 'docs', isChecked: false, addedBy: 'ai' },
    { id: 'doc-6', text: "Pediatrician Name & Number", category: 'docs', isChecked: false, addedBy: 'ai' },
    { id: 'doc-7', text: "Medical Records if Needed", category: 'docs', isChecked: false, addedBy: 'ai' },

    // Mom Essentials
    { id: 'mom-1', text: "Comfortable Robe", category: 'mom', isChecked: false, addedBy: 'ai' },
    { id: 'mom-2', text: "Non-Slip Slippers or Socks", category: 'mom', isChecked: false, addedBy: 'ai' },
    { id: 'mom-3', text: "Nursing Bras (2-3)", category: 'mom', isChecked: false, addedBy: 'ai' },
    { id: 'mom-4', text: "Nursing Pads", category: 'mom', isChecked: false, addedBy: 'ai' },
    { id: 'mom-5', text: "Comfortable Going-Home Outfit (maternity size)", category: 'mom', isChecked: false, addedBy: 'ai' },
    { id: 'mom-6', text: "Toiletries (Travel Size)", category: 'mom', isChecked: false, addedBy: 'ai' },
    { id: 'mom-7', text: "Hair Ties & Headband", category: 'mom', isChecked: false, addedBy: 'ai' },
    { id: 'mom-8', text: "Lip Balm (lips get dry during labor)", category: 'mom', isChecked: false, addedBy: 'ai' },
    { id: 'mom-9', text: "Own Pillow (optional but comfy)", category: 'mom', isChecked: false, addedBy: 'ai' },
    { id: 'mom-10', text: "Nipple Cream (Lanolin)", category: 'mom', isChecked: false, addedBy: 'ai' },
    { id: 'mom-11', text: "Phone + Long Charger Cable (10ft)", category: 'mom', isChecked: false, addedBy: 'ai' },
    { id: 'mom-12', text: "Glasses/Contacts (if needed)", category: 'mom', isChecked: false, addedBy: 'ai' },
    { id: 'mom-13', text: "Entertainment (Books, Tablet, Headphones)", category: 'mom', isChecked: false, addedBy: 'ai' },
    { id: 'mom-14', text: "Nursing Pillow", category: 'mom', isChecked: false, addedBy: 'ai' },
    { id: 'mom-15', text: "High-Waisted Underwear (5+ pairs)", category: 'mom', isChecked: false, addedBy: 'ai' },
    { id: 'mom-16', text: "Breast Pump (if bringing your own)", category: 'mom', isChecked: false, addedBy: 'ai' },
    { id: 'mom-17', text: "Face Wash & Moisturizer", category: 'mom', isChecked: false, addedBy: 'ai' },
    { id: 'mom-18', text: "Makeup Bag (for photos)", category: 'mom', isChecked: false, addedBy: 'ai' },
    { id: 'mom-19', text: "Deodorant", category: 'mom', isChecked: false, addedBy: 'ai' },
    { id: 'mom-20', text: "Contact Lens Solution", category: 'mom', isChecked: false, addedBy: 'ai' },

    // Partner
    { id: 'partner-1', text: "Change of Clothes (2 sets)", category: 'partner', isChecked: false, addedBy: 'ai' },
    { id: 'partner-2', text: "Toiletries", category: 'partner', isChecked: false, addedBy: 'ai' },
    { id: 'partner-3', text: "Phone + Long Charger Cable", category: 'partner', isChecked: false, addedBy: 'ai' },
    { id: 'partner-4', text: "Snacks (Protein Bars, Nuts, Jerky)", category: 'partner', isChecked: false, addedBy: 'ai' },
    { id: 'partner-5', text: "Cash (Parking, Vending Machines)", category: 'partner', isChecked: false, addedBy: 'ai' },
    { id: 'partner-6', text: "Pillow from Home", category: 'partner', isChecked: false, addedBy: 'ai' },
    { id: 'partner-7', text: "Camera (if not using phone)", category: 'partner', isChecked: false, addedBy: 'ai' },
    { id: 'partner-8', text: "Bluetooth Speaker (for music during labor)", category: 'partner', isChecked: false, addedBy: 'ai' },
    { id: 'partner-9', text: "Massage Ball or Tennis Ball (for counter-pressure)", category: 'partner', isChecked: false, addedBy: 'ai' },
    { id: 'partner-10', text: "Laptop or Tablet (for waiting)", category: 'partner', isChecked: false, addedBy: 'ai' },
    { id: 'partner-11', text: "Comfortable Shoes", category: 'partner', isChecked: false, addedBy: 'ai' },
    { id: 'partner-12', text: "Blanket (hospital chairs aren't comfy)", category: 'partner', isChecked: false, addedBy: 'ai' },
    { id: 'partner-13', text: "Water Bottle", category: 'partner', isChecked: false, addedBy: 'ai' },
    { id: 'partner-14', text: "Flip Flops for Shower", category: 'partner', isChecked: false, addedBy: 'ai' },
    { id: 'partner-15', text: "Watch or Timer (for contractions)", category: 'partner', isChecked: false, addedBy: 'ai' },

    // Baby
    { id: 'baby-1', text: "Going-Home Outfit (Newborn AND 0-3 month size)", category: 'baby', isChecked: false, addedBy: 'ai' },
    { id: 'baby-2', text: "Swaddle Blanket", category: 'baby', isChecked: false, addedBy: 'ai' },
    { id: 'baby-3', text: "Newborn Hat", category: 'baby', isChecked: false, addedBy: 'ai' },
    { id: 'baby-4', text: "Car Seat (INSTALLED in car)", category: 'baby', isChecked: false, addedBy: 'ai' },
    { id: 'baby-5', text: "Extra Onesie (in case of blowout)", category: 'baby', isChecked: false, addedBy: 'ai' },
    { id: 'baby-6', text: "Socks/Booties", category: 'baby', isChecked: false, addedBy: 'ai' },
    { id: 'baby-7', text: "Receiving Blanket", category: 'baby', isChecked: false, addedBy: 'ai' },
    { id: 'baby-8', text: "Pacifier (if using)", category: 'baby', isChecked: false, addedBy: 'ai' },
    { id: 'baby-9', text: "Mittens (scratch prevention)", category: 'baby', isChecked: false, addedBy: 'ai' },
    { id: 'baby-10', text: "Baby Book (for footprints)", category: 'baby', isChecked: false, addedBy: 'ai' },
];

// --- APPOINTMENT QUESTIONS BY TRIMESTER ---
const APPOINTMENT_QUESTIONS: Record<string, QuestionItem[]> = {
    first: [
        { id: 'q1-1', text: "Is there anything I should avoid eating or doing?", category: 'medical', isAnswered: false, aiGenerated: false },
        { id: 'q1-2', text: "When should I come back for my next appointment?", category: 'logistics', isAnswered: false, aiGenerated: false },
        { id: 'q1-3', text: "What prenatal vitamins do you recommend?", category: 'medical', isAnswered: false, aiGenerated: false },
        { id: 'q1-4', text: "Is my morning sickness normal? When will it ease up?", category: 'medical', isAnswered: false, aiGenerated: false },
        { id: 'q1-5', text: "What symptoms should trigger a call to you?", category: 'medical', isAnswered: false, aiGenerated: false },
        { id: 'q1-6', text: "Is it safe to exercise? What types are best?", category: 'medical', isAnswered: false, aiGenerated: false },
        { id: 'q1-7', text: "Should I be concerned about my fatigue level?", category: 'medical', isAnswered: false, aiGenerated: false },
        { id: 'q1-8', text: "When can we find out the gender?", category: 'baby', isAnswered: false, aiGenerated: false },
        { id: 'q1-9', text: "What genetic screening options do you recommend?", category: 'medical', isAnswered: false, aiGenerated: false },
        { id: 'q1-10', text: "Is it normal to not feel connected to the baby yet?", category: 'baby', isAnswered: false, aiGenerated: false },
    ],
    second: [
        { id: 'q2-1', text: "Is the baby's growth and development on track?", category: 'baby', isAnswered: false, aiGenerated: false },
        { id: 'q2-2', text: "When should I feel the baby move? What's normal?", category: 'baby', isAnswered: false, aiGenerated: false },
        { id: 'q2-3', text: "What should I know about the anatomy scan?", category: 'medical', isAnswered: false, aiGenerated: false },
        { id: 'q2-4', text: "Are there any warning signs I should watch for now?", category: 'medical', isAnswered: false, aiGenerated: false },
        { id: 'q2-5', text: "Is my weight gain appropriate?", category: 'medical', isAnswered: false, aiGenerated: false },
        { id: 'q2-6', text: "What classes do you recommend? (Childbirth, CPR, Breastfeeding)", category: 'logistics', isAnswered: false, aiGenerated: false },
        { id: 'q2-7', text: "When should I pre-register at the hospital?", category: 'logistics', isAnswered: false, aiGenerated: false },
        { id: 'q2-8', text: "Is it normal to have round ligament pain?", category: 'medical', isAnswered: false, aiGenerated: false },
        { id: 'q2-9', text: "Can you recommend ways to manage heartburn?", category: 'medical', isAnswered: false, aiGenerated: false },
        { id: 'q2-10', text: "When should we start thinking about a birth plan?", category: 'logistics', isAnswered: false, aiGenerated: false },
    ],
    third: [
        { id: 'q3-1', text: "When should we go to the hospital?", category: 'logistics', isAnswered: false, aiGenerated: false },
        { id: 'q3-2', text: "What are your policies on induction?", category: 'medical', isAnswered: false, aiGenerated: false },
        { id: 'q3-3', text: "What pain management options are available?", category: 'medical', isAnswered: false, aiGenerated: false },
        { id: 'q3-4', text: "What is your C-section rate?", category: 'medical', isAnswered: false, aiGenerated: false },
        { id: 'q3-5', text: "Can you review our birth plan?", category: 'logistics', isAnswered: false, aiGenerated: false },
        { id: 'q3-6', text: "How can I count kicks and what's concerning?", category: 'baby', isAnswered: false, aiGenerated: false },
        { id: 'q3-7', text: "Is baby head down? What if they don't flip?", category: 'baby', isAnswered: false, aiGenerated: false },
        { id: 'q3-8', text: "What are Braxton Hicks vs real contractions?", category: 'medical', isAnswered: false, aiGenerated: false },
        { id: 'q3-9', text: "When should we have the hospital bag packed?", category: 'logistics', isAnswered: false, aiGenerated: false },
        { id: 'q3-10', text: "What happens if I go past my due date?", category: 'medical', isAnswered: false, aiGenerated: false },
        { id: 'q3-11', text: "Who will deliver if you're not available?", category: 'logistics', isAnswered: false, aiGenerated: false },
        { id: 'q3-12', text: "What postpartum support do you recommend?", category: 'medical', isAnswered: false, aiGenerated: false },
    ]
};

// --- BABY NAMES DATABASE (Expanded with Cultural Categories) ---
const BABY_NAMES: NameIdea[] = [
    // SCANDINAVIAN (Swedish, Norwegian, Danish)
    { name: "Astrid", gender: "girl", origin: "Swedish", meaning: "Divinely Beautiful" },
    { name: "Saga", gender: "girl", origin: "Swedish", meaning: "Story, Fairy Tale" },
    { name: "Freja", gender: "girl", origin: "Swedish", meaning: "Lady, Noble Woman" },
    { name: "Ingrid", gender: "girl", origin: "Swedish", meaning: "Beautiful, Beloved" },
    { name: "Sigrid", gender: "girl", origin: "Swedish", meaning: "Beautiful Victory" },
    { name: "Linnea", gender: "girl", origin: "Swedish", meaning: "Twinflower" },
    { name: "Maja", gender: "girl", origin: "Swedish", meaning: "Pearl, Beloved" },
    { name: "Elsa", gender: "girl", origin: "Swedish", meaning: "Pledged to God" },
    { name: "Nora", gender: "girl", origin: "Norwegian", meaning: "Light, Honor" },
    { name: "Solveig", gender: "girl", origin: "Norwegian", meaning: "Sun Strength" },
    { name: "Erik", gender: "boy", origin: "Swedish", meaning: "Eternal Ruler" },
    { name: "Lars", gender: "boy", origin: "Swedish", meaning: "Crowned with Laurel" },
    { name: "Sven", gender: "boy", origin: "Swedish", meaning: "Young Warrior" },
    { name: "Anders", gender: "boy", origin: "Swedish", meaning: "Strong, Manly" },
    { name: "Bjorn", gender: "boy", origin: "Swedish", meaning: "Bear" },
    { name: "Axel", gender: "boy", origin: "Swedish", meaning: "Father of Peace" },
    { name: "Magnus", gender: "boy", origin: "Swedish", meaning: "Great" },
    { name: "Leif", gender: "boy", origin: "Norwegian", meaning: "Descendant, Heir" },
    { name: "Odin", gender: "boy", origin: "Norse", meaning: "God of Wisdom" },
    { name: "Thor", gender: "boy", origin: "Norse", meaning: "Thunder" },

    // IRISH/CELTIC
    { name: "Aoife", gender: "girl", origin: "Irish", meaning: "Beauty, Radiance" },
    { name: "Saoirse", gender: "girl", origin: "Irish", meaning: "Freedom" },
    { name: "Siobhan", gender: "girl", origin: "Irish", meaning: "God is Gracious" },
    { name: "Niamh", gender: "girl", origin: "Irish", meaning: "Bright, Radiant" },
    { name: "Ciara", gender: "girl", origin: "Irish", meaning: "Dark-haired" },
    { name: "Maeve", gender: "girl", origin: "Irish", meaning: "Intoxicating" },
    { name: "Aisling", gender: "girl", origin: "Irish", meaning: "Dream, Vision" },
    { name: "Roisin", gender: "girl", origin: "Irish", meaning: "Little Rose" },
    { name: "Declan", gender: "boy", origin: "Irish", meaning: "Man of Prayer" },
    { name: "Cillian", gender: "boy", origin: "Irish", meaning: "Church, War" },
    { name: "Liam", gender: "boy", origin: "Irish", meaning: "Strong-willed Warrior" },
    { name: "Seamus", gender: "boy", origin: "Irish", meaning: "Supplanter" },
    { name: "Padraig", gender: "boy", origin: "Irish", meaning: "Noble" },
    { name: "Eoin", gender: "boy", origin: "Irish", meaning: "God is Gracious" },
    { name: "Connor", gender: "boy", origin: "Irish", meaning: "Lover of Wolves" },
    { name: "Ronan", gender: "boy", origin: "Irish", meaning: "Little Seal" },

    // JAPANESE
    { name: "Sakura", gender: "girl", origin: "Japanese", meaning: "Cherry Blossom" },
    { name: "Yuki", gender: "neutral", origin: "Japanese", meaning: "Snow, Happiness" },
    { name: "Hana", gender: "girl", origin: "Japanese", meaning: "Flower" },
    { name: "Aiko", gender: "girl", origin: "Japanese", meaning: "Beloved Child" },
    { name: "Mei", gender: "girl", origin: "Japanese", meaning: "Beautiful" },
    { name: "Rin", gender: "girl", origin: "Japanese", meaning: "Dignified" },
    { name: "Haru", gender: "neutral", origin: "Japanese", meaning: "Spring" },
    { name: "Naomi", gender: "girl", origin: "Japanese", meaning: "Honest Beauty" },
    { name: "Kai", gender: "neutral", origin: "Japanese", meaning: "Ocean" },
    { name: "Ren", gender: "neutral", origin: "Japanese", meaning: "Lotus, Love" },
    { name: "Hiroshi", gender: "boy", origin: "Japanese", meaning: "Generous" },
    { name: "Kenji", gender: "boy", origin: "Japanese", meaning: "Intelligent Ruler" },
    { name: "Ryu", gender: "boy", origin: "Japanese", meaning: "Dragon" },
    { name: "Akira", gender: "neutral", origin: "Japanese", meaning: "Bright, Clear" },

    // SPANISH/LATIN AMERICAN
    { name: "Sofia", gender: "girl", origin: "Spanish", meaning: "Wisdom" },
    { name: "Isabella", gender: "girl", origin: "Spanish", meaning: "Devoted to God" },
    { name: "Valentina", gender: "girl", origin: "Spanish", meaning: "Strong, Healthy" },
    { name: "Camila", gender: "girl", origin: "Spanish", meaning: "Young Ceremonial Attendant" },
    { name: "Lucia", gender: "girl", origin: "Spanish", meaning: "Light" },
    { name: "Elena", gender: "girl", origin: "Spanish", meaning: "Bright, Shining Light" },
    { name: "Alma", gender: "girl", origin: "Spanish", meaning: "Soul" },
    { name: "Paloma", gender: "girl", origin: "Spanish", meaning: "Dove" },
    { name: "Mateo", gender: "boy", origin: "Spanish", meaning: "Gift of God" },
    { name: "Santiago", gender: "boy", origin: "Spanish", meaning: "Saint James" },
    { name: "Diego", gender: "boy", origin: "Spanish", meaning: "Supplanter" },
    { name: "Rafael", gender: "boy", origin: "Spanish", meaning: "God Has Healed" },
    { name: "Carlos", gender: "boy", origin: "Spanish", meaning: "Free Man" },
    { name: "Alejandro", gender: "boy", origin: "Spanish", meaning: "Defender" },

    // AFRICAN (Various Regions)
    { name: "Amara", gender: "girl", origin: "African (Igbo)", meaning: "Grace, Mercy" },
    { name: "Zara", gender: "girl", origin: "African (Arabic)", meaning: "Princess, Flower" },
    { name: "Imani", gender: "girl", origin: "African (Swahili)", meaning: "Faith" },
    { name: "Nia", gender: "girl", origin: "African (Swahili)", meaning: "Purpose" },
    { name: "Amina", gender: "girl", origin: "African (Arabic)", meaning: "Trustworthy" },
    { name: "Ayana", gender: "girl", origin: "African (Ethiopian)", meaning: "Beautiful Flower" },
    { name: "Zuri", gender: "girl", origin: "African (Swahili)", meaning: "Beautiful" },
    { name: "Kwame", gender: "boy", origin: "African (Ghanaian)", meaning: "Born on Saturday" },
    { name: "Kofi", gender: "boy", origin: "African (Ghanaian)", meaning: "Born on Friday" },
    { name: "Jabari", gender: "boy", origin: "African (Swahili)", meaning: "Brave" },
    { name: "Malik", gender: "boy", origin: "African (Arabic)", meaning: "King" },
    { name: "Omari", gender: "boy", origin: "African (Swahili)", meaning: "God is the Highest" },

    // INDIAN/SOUTH ASIAN
    { name: "Aria", gender: "girl", origin: "Indian (Sanskrit)", meaning: "Noble, Air" },
    { name: "Priya", gender: "girl", origin: "Indian (Sanskrit)", meaning: "Beloved" },
    { name: "Maya", gender: "girl", origin: "Indian (Sanskrit)", meaning: "Illusion, Magic" },
    { name: "Ananya", gender: "girl", origin: "Indian (Sanskrit)", meaning: "Unique" },
    { name: "Devi", gender: "girl", origin: "Indian (Sanskrit)", meaning: "Goddess" },
    { name: "Lakshmi", gender: "girl", origin: "Indian (Sanskrit)", meaning: "Fortune" },
    { name: "Arjun", gender: "boy", origin: "Indian (Sanskrit)", meaning: "Bright, Shining" },
    { name: "Dev", gender: "boy", origin: "Indian (Sanskrit)", meaning: "God" },
    { name: "Ravi", gender: "boy", origin: "Indian (Sanskrit)", meaning: "Sun" },
    { name: "Arun", gender: "boy", origin: "Indian (Sanskrit)", meaning: "Dawn" },
    { name: "Vikram", gender: "boy", origin: "Indian (Sanskrit)", meaning: "Valorous" },
    { name: "Nikhil", gender: "boy", origin: "Indian (Sanskrit)", meaning: "Complete, Whole" },

    // FRENCH
    { name: "Amélie", gender: "girl", origin: "French", meaning: "Hardworking" },
    { name: "Colette", gender: "girl", origin: "French", meaning: "Victory of the People" },
    { name: "Genevieve", gender: "girl", origin: "French", meaning: "Tribe Woman" },
    { name: "Margot", gender: "girl", origin: "French", meaning: "Pearl" },
    { name: "Odette", gender: "girl", origin: "French", meaning: "Wealthy" },
    { name: "Simone", gender: "girl", origin: "French", meaning: "One Who Hears" },
    { name: "Julien", gender: "boy", origin: "French", meaning: "Youthful" },
    { name: "Olivier", gender: "boy", origin: "French", meaning: "Olive Tree" },
    { name: "Rémy", gender: "boy", origin: "French", meaning: "Oarsman" },
    { name: "Théo", gender: "boy", origin: "French", meaning: "Gift of God" },

    // GERMAN
    { name: "Greta", gender: "girl", origin: "German", meaning: "Pearl" },
    { name: "Liesel", gender: "girl", origin: "German", meaning: "Pledged to God" },
    { name: "Marta", gender: "girl", origin: "German", meaning: "Lady, Mistress" },
    { name: "Klaus", gender: "boy", origin: "German", meaning: "Victory of the People" },
    { name: "Hans", gender: "boy", origin: "German", meaning: "God is Gracious" },
    { name: "Fritz", gender: "boy", origin: "German", meaning: "Peaceful Ruler" },

    // GREEK
    { name: "Athena", gender: "girl", origin: "Greek", meaning: "Goddess of Wisdom" },
    { name: "Daphne", gender: "girl", origin: "Greek", meaning: "Laurel Tree" },
    { name: "Penelope", gender: "girl", origin: "Greek", meaning: "Weaver" },
    { name: "Thea", gender: "girl", origin: "Greek", meaning: "Goddess" },
    { name: "Calliope", gender: "girl", origin: "Greek", meaning: "Beautiful Voice" },
    { name: "Dimitri", gender: "boy", origin: "Greek", meaning: "Follower of Demeter" },
    { name: "Nikolaos", gender: "boy", origin: "Greek", meaning: "Victory of the People" },
    { name: "Stavros", gender: "boy", origin: "Greek", meaning: "Cross" },

    // HEBREW/JEWISH
    { name: "Miriam", gender: "girl", origin: "Hebrew", meaning: "Wished-for Child" },
    { name: "Naomi", gender: "girl", origin: "Hebrew", meaning: "Pleasantness" },
    { name: "Ruth", gender: "girl", origin: "Hebrew", meaning: "Friend, Companion" },
    { name: "Esther", gender: "girl", origin: "Hebrew", meaning: "Star" },
    { name: "Hannah", gender: "girl", origin: "Hebrew", meaning: "Grace" },
    { name: "Elijah", gender: "boy", origin: "Hebrew", meaning: "My God is Yahweh" },
    { name: "Noah", gender: "boy", origin: "Hebrew", meaning: "Rest, Comfort" },
    { name: "Levi", gender: "boy", origin: "Hebrew", meaning: "Joined, Attached" },
    { name: "Asher", gender: "boy", origin: "Hebrew", meaning: "Happy, Blessed" },
    { name: "Micah", gender: "boy", origin: "Hebrew", meaning: "Who is Like God" },

    // KOREAN
    { name: "Soo-Yeon", gender: "girl", origin: "Korean", meaning: "Lotus, Graceful" },
    { name: "Ji-Eun", gender: "girl", origin: "Korean", meaning: "Wisdom, Kindness" },
    { name: "Hae-Won", gender: "girl", origin: "Korean", meaning: "Graceful Garden" },
    { name: "Min-Ji", gender: "girl", origin: "Korean", meaning: "Clever, Beautiful" },
    { name: "Yuna", gender: "girl", origin: "Korean", meaning: "Shine, Softness" },
    { name: "Seung-Hyun", gender: "boy", origin: "Korean", meaning: "Winning, Virtuous" },
    { name: "Ji-Hoon", gender: "boy", origin: "Korean", meaning: "Wisdom, Rank" },
    { name: "Min-Jun", gender: "boy", origin: "Korean", meaning: "Quick, Talented" },
    { name: "Tae-Hyun", gender: "boy", origin: "Korean", meaning: "Great, Worthy" },
    { name: "Jae-Min", gender: "boy", origin: "Korean", meaning: "Talented, Clever" },

    // CHINESE
    { name: "Mei-Lin", gender: "girl", origin: "Chinese", meaning: "Beautiful Jade" },
    { name: "Xiu-Ying", gender: "girl", origin: "Chinese", meaning: "Elegant Flower" },
    { name: "Li-Hua", gender: "girl", origin: "Chinese", meaning: "Pear Blossom" },
    { name: "Jing", gender: "neutral", origin: "Chinese", meaning: "Quiet, Still" },
    { name: "Wei", gender: "neutral", origin: "Chinese", meaning: "Greatness" },
    { name: "Chen", gender: "boy", origin: "Chinese", meaning: "Morning" },
    { name: "Ming", gender: "boy", origin: "Chinese", meaning: "Bright, Luminous" },
    { name: "Hao", gender: "boy", origin: "Chinese", meaning: "Good, Perfect" },
    { name: "Jun", gender: "boy", origin: "Chinese", meaning: "King, Ruler" },
    { name: "Lei", gender: "boy", origin: "Chinese", meaning: "Thunder" },

    // ARABIC
    { name: "Fatima", gender: "girl", origin: "Arabic", meaning: "Captivating" },
    { name: "Aisha", gender: "girl", origin: "Arabic", meaning: "Alive, Prosperous" },
    { name: "Layla", gender: "girl", origin: "Arabic", meaning: "Night" },
    { name: "Noor", gender: "neutral", origin: "Arabic", meaning: "Light" },
    { name: "Salma", gender: "girl", origin: "Arabic", meaning: "Peace" },
    { name: "Yusuf", gender: "boy", origin: "Arabic", meaning: "God Increases" },
    { name: "Omar", gender: "boy", origin: "Arabic", meaning: "Long-lived" },
    { name: "Khalil", gender: "boy", origin: "Arabic", meaning: "Friend" },
    { name: "Tariq", gender: "boy", origin: "Arabic", meaning: "Morning Star" },
    { name: "Zayd", gender: "boy", origin: "Arabic", meaning: "Growth, Abundance" },

    // RUSSIAN
    { name: "Anastasia", gender: "girl", origin: "Russian", meaning: "Resurrection" },
    { name: "Natasha", gender: "girl", origin: "Russian", meaning: "Birthday" },
    { name: "Katerina", gender: "girl", origin: "Russian", meaning: "Pure" },
    { name: "Svetlana", gender: "girl", origin: "Russian", meaning: "Light" },
    { name: "Tatiana", gender: "girl", origin: "Russian", meaning: "Fairy Queen" },
    { name: "Dmitri", gender: "boy", origin: "Russian", meaning: "Follower of Demeter" },
    { name: "Nikolai", gender: "boy", origin: "Russian", meaning: "Victory of the People" },
    { name: "Alexei", gender: "boy", origin: "Russian", meaning: "Defender" },
    { name: "Ivan", gender: "boy", origin: "Russian", meaning: "God is Gracious" },
    { name: "Mikhail", gender: "boy", origin: "Russian", meaning: "Who is Like God" },

    // ITALIAN
    { name: "Giulia", gender: "girl", origin: "Italian", meaning: "Youthful" },
    { name: "Francesca", gender: "girl", origin: "Italian", meaning: "Free One" },
    { name: "Chiara", gender: "girl", origin: "Italian", meaning: "Bright, Clear" },
    { name: "Valentina", gender: "girl", origin: "Italian", meaning: "Strong, Healthy" },
    { name: "Bianca", gender: "girl", origin: "Italian", meaning: "White, Pure" },
    { name: "Marco", gender: "boy", origin: "Italian", meaning: "Warlike" },
    { name: "Lorenzo", gender: "boy", origin: "Italian", meaning: "From Laurentum" },
    { name: "Matteo", gender: "boy", origin: "Italian", meaning: "Gift of God" },
    { name: "Luca", gender: "boy", origin: "Italian", meaning: "Light" },
    { name: "Giovanni", gender: "boy", origin: "Italian", meaning: "God is Gracious" },

    // POLISH
    { name: "Zofia", gender: "girl", origin: "Polish", meaning: "Wisdom" },
    { name: "Wiktoria", gender: "girl", origin: "Polish", meaning: "Victory" },
    { name: "Kasia", gender: "girl", origin: "Polish", meaning: "Pure" },
    { name: "Maja", gender: "girl", origin: "Polish", meaning: "Great" },
    { name: "Jakub", gender: "boy", origin: "Polish", meaning: "Supplanter" },
    { name: "Piotr", gender: "boy", origin: "Polish", meaning: "Rock" },
    { name: "Kacper", gender: "boy", origin: "Polish", meaning: "Treasurer" },
    { name: "Tomasz", gender: "boy", origin: "Polish", meaning: "Twin" },

    // BRAZILIAN/PORTUGUESE
    { name: "Beatriz", gender: "girl", origin: "Portuguese", meaning: "Voyager" },
    { name: "Mariana", gender: "girl", origin: "Portuguese", meaning: "Of the Sea" },
    { name: "Larissa", gender: "girl", origin: "Portuguese", meaning: "Cheerful" },
    { name: "Gabriela", gender: "girl", origin: "Portuguese", meaning: "God is My Strength" },
    { name: "Joao", gender: "boy", origin: "Portuguese", meaning: "God is Gracious" },
    { name: "Lucas", gender: "boy", origin: "Portuguese", meaning: "Light" },
    { name: "Pedro", gender: "boy", origin: "Portuguese", meaning: "Rock" },
    { name: "Thiago", gender: "boy", origin: "Portuguese", meaning: "Supplanter" },

    // NATIVE AMERICAN
    { name: "Aiyana", gender: "girl", origin: "Native American", meaning: "Eternal Bloom" },
    { name: "Chenoa", gender: "girl", origin: "Native American", meaning: "Dove" },
    { name: "Kaya", gender: "girl", origin: "Native American", meaning: "Elder Sister" },
    { name: "Winona", gender: "girl", origin: "Native American (Lakota)", meaning: "First-born Daughter" },
    { name: "Dakota", gender: "neutral", origin: "Native American (Sioux)", meaning: "Friend" },
    { name: "Koda", gender: "boy", origin: "Native American", meaning: "Friend" },
    { name: "Takoda", gender: "boy", origin: "Native American (Sioux)", meaning: "Friend to Everyone" },
    { name: "Chayton", gender: "boy", origin: "Native American (Sioux)", meaning: "Falcon" },

    // HAWAIIAN
    { name: "Leilani", gender: "girl", origin: "Hawaiian", meaning: "Heavenly Flower" },
    { name: "Malia", gender: "girl", origin: "Hawaiian", meaning: "Calm Waters" },
    { name: "Kailani", gender: "girl", origin: "Hawaiian", meaning: "Sea and Sky" },
    { name: "Moana", gender: "girl", origin: "Hawaiian", meaning: "Ocean" },
    { name: "Keanu", gender: "boy", origin: "Hawaiian", meaning: "Cool Breeze" },
    { name: "Makoa", gender: "boy", origin: "Hawaiian", meaning: "Brave" },
    { name: "Kaimana", gender: "boy", origin: "Hawaiian", meaning: "Power of the Ocean" },
    { name: "Kai", gender: "neutral", origin: "Hawaiian", meaning: "Sea" },

    // FILIPINO
    { name: "Maria", gender: "girl", origin: "Filipino", meaning: "Beloved" },
    { name: "Luz", gender: "girl", origin: "Filipino", meaning: "Light" },
    { name: "Amihan", gender: "girl", origin: "Filipino", meaning: "North Wind" },
    { name: "Tala", gender: "girl", origin: "Filipino", meaning: "Star" },
    { name: "Jose", gender: "boy", origin: "Filipino", meaning: "God Will Add" },
    { name: "Gabriel", gender: "boy", origin: "Filipino", meaning: "God is My Strength" },
    { name: "Miguel", gender: "boy", origin: "Filipino", meaning: "Who is Like God" },
    { name: "Rafael", gender: "boy", origin: "Filipino", meaning: "God Has Healed" },

    // THAI
    { name: "Sirinya", gender: "girl", origin: "Thai", meaning: "Wealthy Maiden" },
    { name: "Niran", gender: "boy", origin: "Thai", meaning: "Eternal" },
    { name: "Sunan", gender: "boy", origin: "Thai", meaning: "Good Word" },
    { name: "Mali", gender: "girl", origin: "Thai", meaning: "Jasmine Flower" },

    // VIETNAMESE
    { name: "Linh", gender: "neutral", origin: "Vietnamese", meaning: "Spirit" },
    { name: "Nguyen", gender: "neutral", origin: "Vietnamese", meaning: "Original" },
    { name: "Anh", gender: "neutral", origin: "Vietnamese", meaning: "Bright" },
    { name: "Hue", gender: "girl", origin: "Vietnamese", meaning: "Lily" },
    { name: "Minh", gender: "boy", origin: "Vietnamese", meaning: "Bright, Clever" },

    // Modern/Trendy - Girls
    { name: "Luna", gender: "girl", origin: "Latin", meaning: "Moon" },
    { name: "Aria", gender: "girl", origin: "Italian", meaning: "Air, Song, or Melody" },
    { name: "Nova", gender: "girl", origin: "Latin", meaning: "New" },
    { name: "Willow", gender: "girl", origin: "English", meaning: "Graceful, Slender" },
    { name: "Ivy", gender: "girl", origin: "English", meaning: "Faithfulness" },
    { name: "Aurora", gender: "girl", origin: "Latin", meaning: "Dawn" },
    { name: "Hazel", gender: "girl", origin: "English", meaning: "The Hazel Tree" },
    { name: "Cora", gender: "girl", origin: "Greek", meaning: "Maiden" },
    { name: "Freya", gender: "girl", origin: "Norse", meaning: "Noble Woman" },
    { name: "Isla", gender: "girl", origin: "Scottish", meaning: "Island" },
    { name: "Stella", gender: "girl", origin: "Latin", meaning: "Star" },
    { name: "Violet", gender: "girl", origin: "Latin", meaning: "Purple Flower" },

    // Modern/Trendy - Boys
    { name: "Milo", gender: "boy", origin: "German", meaning: "Soldier or Merciful" },
    { name: "Atlas", gender: "boy", origin: "Greek", meaning: "Bearer of the Heavens" },
    { name: "Jasper", gender: "boy", origin: "Persian", meaning: "Treasurer" },
    { name: "Felix", gender: "boy", origin: "Latin", meaning: "Happy, Fortunate" },
    { name: "Leo", gender: "boy", origin: "Latin", meaning: "Lion" },
    { name: "Oscar", gender: "boy", origin: "Irish/Norse", meaning: "Deer Friend" },
    { name: "Finn", gender: "boy", origin: "Irish", meaning: "Fair" },
    { name: "Silas", gender: "boy", origin: "Latin", meaning: "Forest, Woods" },
    { name: "Ezra", gender: "boy", origin: "Hebrew", meaning: "Helper" },
    { name: "August", gender: "boy", origin: "Latin", meaning: "Great, Magnificent" },
    { name: "Theodore", gender: "boy", origin: "Greek", meaning: "Gift of God" },
    { name: "Oliver", gender: "boy", origin: "Latin", meaning: "Olive Tree" },

    // Classic - Girls
    { name: "Elizabeth", gender: "girl", origin: "Hebrew", meaning: "Pledged to God" },
    { name: "Charlotte", gender: "girl", origin: "French", meaning: "Free Woman" },
    { name: "Eleanor", gender: "girl", origin: "Greek", meaning: "Bright, Shining One" },
    { name: "Catherine", gender: "girl", origin: "Greek", meaning: "Pure" },
    { name: "Margaret", gender: "girl", origin: "Greek", meaning: "Pearl" },
    { name: "Victoria", gender: "girl", origin: "Latin", meaning: "Victory" },
    { name: "Grace", gender: "girl", origin: "Latin", meaning: "Grace, Blessing" },
    { name: "Rose", gender: "girl", origin: "Latin", meaning: "Rose Flower" },
    { name: "Alice", gender: "girl", origin: "German", meaning: "Noble" },
    { name: "Claire", gender: "girl", origin: "French", meaning: "Clear, Bright" },

    // Classic - Boys
    { name: "James", gender: "boy", origin: "Hebrew", meaning: "Supplanter" },
    { name: "William", gender: "boy", origin: "German", meaning: "Resolute Protector" },
    { name: "Henry", gender: "boy", origin: "German", meaning: "Ruler of the Home" },
    { name: "Charles", gender: "boy", origin: "German", meaning: "Free Man" },
    { name: "George", gender: "boy", origin: "Greek", meaning: "Farmer" },
    { name: "Edward", gender: "boy", origin: "English", meaning: "Wealthy Guardian" },
    { name: "Thomas", gender: "boy", origin: "Aramaic", meaning: "Twin" },
    { name: "Benjamin", gender: "boy", origin: "Hebrew", meaning: "Son of the Right Hand" },
    { name: "Alexander", gender: "boy", origin: "Greek", meaning: "Defender of the People" },
    { name: "Daniel", gender: "boy", origin: "Hebrew", meaning: "God is My Judge" },

    // Nature-Inspired
    { name: "River", gender: "neutral", origin: "English", meaning: "Flowing Body of Water" },
    { name: "Sage", gender: "neutral", origin: "Latin", meaning: "Wise One" },
    { name: "Rowan", gender: "neutral", origin: "Celtic", meaning: "Little Red One" },
    { name: "Wren", gender: "neutral", origin: "English", meaning: "Small Bird" },
    { name: "Juniper", gender: "girl", origin: "Latin", meaning: "Evergreen Shrub" },
    { name: "Forrest", gender: "boy", origin: "French", meaning: "Woodland" },
    { name: "Aspen", gender: "neutral", origin: "English", meaning: "Quaking Tree" },
    { name: "Ocean", gender: "neutral", origin: "English", meaning: "The Sea" },

    // Unique
    { name: "Zephyr", gender: "boy", origin: "Greek", meaning: "West Wind" },
    { name: "Orion", gender: "boy", origin: "Greek", meaning: "Rising in the Sky" },
    { name: "Seren", gender: "girl", origin: "Welsh", meaning: "Star" },
    { name: "Indigo", gender: "neutral", origin: "Greek", meaning: "Deep Blue Color" },
    { name: "Phoenix", gender: "neutral", origin: "Greek", meaning: "Mythical Reborn Bird" },
    { name: "Lyra", gender: "girl", origin: "Greek", meaning: "Lyre, Constellation" },
];

// --- BIRTH PLAN SECTIONS (EXPANDED) ---

// Comprehensive preference categories with mappings
export const BIRTH_PLAN_PREFERENCE_CATEGORIES = {
    environment: {
        title: "Environment",
        icon: "🏥",
        options: [
            "Dim lighting",
            "Music/playlist allowed",
            "Aromatherapy",
            "Quiet atmosphere",
            "Freedom to move around",
            "Use of birth ball",
            "Birthing tub/water labor"
        ]
    },
    painManagement: {
        title: "Pain Management",
        icon: "💪",
        options: [
            "Natural methods first",
            "Epidural if requested",
            "Nitrous oxide (laughing gas)",
            "Hydrotherapy",
            "Counter-pressure massage",
            "TENS machine",
            "No pain meds unless I ask"
        ]
    },
    laborDelivery: {
        title: "Labor & Delivery",
        icon: "👶",
        options: [
            "Delayed cord clamping",
            "Spontaneous pushing",
            "Intermittent monitoring",
            "Freedom to eat/drink",
            "Avoid induction if possible",
            "Mirror to watch birth",
            "Catch my own baby"
        ]
    },
    afterBirth: {
        title: "After Birth",
        icon: "🤱",
        options: [
            "Immediate skin-to-skin",
            "Golden hour (1 hr bonding)",
            "Partner cuts cord",
            "Delayed bathing",
            "Breastfeeding in first hour",
            "Keep baby in room always",
            "Placenta encapsulation"
        ]
    },
    specialCircumstances: {
        title: "Special Circumstances",
        icon: "⚕️",
        options: [
            "VBAC attempt",
            "GBS positive protocol",
            "Gestational diabetes",
            "Preeclampsia history",
            "Multiples (twins+)",
            "Breech presentation",
            "Induction planned"
        ]
    },
    familyPreferences: {
        title: "Family & Support",
        icon: "👨‍👩‍👧",
        options: [
            "Partner present throughout",
            "Doula present",
            "Photographer/videographer",
            "No visitors until ready",
            "Sibling(s) present",
            "Gender reveal at birth",
            "Private room requested"
        ]
    },
    cSection: {
        title: "If C-Section Needed",
        icon: "🏥",
        options: [
            "Partner in OR",
            "Clear drape to see baby",
            "Immediate skin-to-skin in OR",
            "Narration of procedure",
            "Music in OR",
            "Partner stays with baby",
            "Gentle/family-centered c-section"
        ]
    }
};

// Section templates with partner roles - maps to selected preferences
const BIRTH_PLAN_SECTIONS_FULL: BirthPlanSection[] = [
    {
        id: 'env',
        title: "Labor Environment",
        preferences: [],
        partnerRole: "Control the environment - dim lights, set up music, guard the door from unnecessary visitors. Speak softly and be a calm presence."
    },
    {
        id: 'pain',
        title: "Pain Management",
        preferences: [],
        partnerRole: "Be hands-on with massage and counter-pressure. Remind her to breathe. Advocate for her wishes if staff offers medication she didn't request."
    },
    {
        id: 'labor',
        title: "Labor & Delivery",
        preferences: [],
        partnerRole: "Help her change positions. Remind staff of birth plan preferences. Ask 'Is this medically necessary right now?' if interventions are suggested."
    },
    {
        id: 'birth',
        title: "Immediately After Birth",
        preferences: [],
        partnerRole: "Announce the sex if it was a surprise. Take photos/video if requested. Ensure skin-to-skin happens. Help initiate first feeding."
    },
    {
        id: 'baby',
        title: "Baby Care Preferences",
        preferences: [],
        partnerRole: "Stay with baby for any procedures. Ask questions on her behalf while she rests. Handle paperwork and birth certificate info."
    },
    {
        id: 'family',
        title: "Family & Visitors",
        preferences: [],
        partnerRole: "Be the gatekeeper for visitors. Communicate boundaries to family. Ensure the bonding time is protected."
    },
    {
        id: 'csection',
        title: "If C-Section Needed",
        preferences: [],
        partnerRole: "Stay calm. Sit by her head, hold her hand, talk to her. Describe what you see when baby is born. Go with baby if temporarily separated."
    },
    {
        id: 'special',
        title: "Special Medical Considerations",
        preferences: [],
        partnerRole: "Know the protocols. Be ready to advocate. Ask clarifying questions. Keep notes of what the medical team says."
    }
];

// Map preferences to sections
const PREFERENCE_TO_SECTION_MAP: Record<string, string> = {
    // Environment
    "Dim lighting": "env",
    "Music/playlist allowed": "env",
    "Aromatherapy": "env",
    "Quiet atmosphere": "env",
    "Freedom to move around": "env",
    "Use of birth ball": "env",
    "Birthing tub/water labor": "env",

    // Pain Management
    "Natural methods first": "pain",
    "Epidural if requested": "pain",
    "Nitrous oxide (laughing gas)": "pain",
    "Hydrotherapy": "pain",
    "Counter-pressure massage": "pain",
    "TENS machine": "pain",
    "No pain meds unless I ask": "pain",

    // Labor & Delivery
    "Delayed cord clamping": "labor",
    "Spontaneous pushing": "labor",
    "Intermittent monitoring": "labor",
    "Freedom to eat/drink": "labor",
    "Avoid induction if possible": "labor",
    "Mirror to watch birth": "labor",
    "Catch my own baby": "labor",

    // After Birth
    "Immediate skin-to-skin": "birth",
    "Golden hour (1 hr bonding)": "birth",
    "Partner cuts cord": "birth",
    "Delayed bathing": "birth",
    "Breastfeeding in first hour": "birth",
    "Keep baby in room always": "baby",
    "Placenta encapsulation": "birth",

    // Family
    "Partner present throughout": "family",
    "Doula present": "family",
    "Photographer/videographer": "family",
    "No visitors until ready": "family",
    "Sibling(s) present": "family",
    "Gender reveal at birth": "birth",
    "Private room requested": "family",

    // C-Section
    "Partner in OR": "csection",
    "Clear drape to see baby": "csection",
    "Immediate skin-to-skin in OR": "csection",
    "Narration of procedure": "csection",
    "Music in OR": "csection",
    "Partner stays with baby": "csection",
    "Gentle/family-centered c-section": "csection",

    // Special Circumstances
    "VBAC attempt": "special",
    "GBS positive protocol": "special",
    "Gestational diabetes": "special",
    "Preeclampsia history": "special",
    "Multiples (twins+)": "special",
    "Breech presentation": "special",
    "Induction planned": "special",

    // Legacy preferences (for backward compatibility)
    "Golden hour (skin-to-skin)": "birth",
    "No episiotomy": "labor",
    "Music playlist": "env"
};

// Generate contextual partner roles based on preferences
const generatePartnerRole = (sectionId: string, preferences: string[]): string => {
    const roleTemplates: Record<string, (prefs: string[]) => string> = {
        env: (prefs) => {
            const roles = ["Control the environment - be her calm anchor."];
            if (prefs.includes("Dim lighting")) roles.push("Keep lights low.");
            if (prefs.includes("Music/playlist allowed")) roles.push("Manage the playlist.");
            if (prefs.includes("Aromatherapy")) roles.push("Handle essential oils/diffuser.");
            if (prefs.includes("Quiet atmosphere")) roles.push("Guard the door from unnecessary visitors.");
            if (prefs.includes("Birthing tub/water labor")) roles.push("Monitor water temperature, help her in/out safely.");
            return roles.join(" ");
        },
        pain: (prefs) => {
            const roles = ["Be hands-on and attentive."];
            if (prefs.includes("Counter-pressure massage")) roles.push("Apply firm hip/back pressure during contractions.");
            if (prefs.includes("Natural methods first")) roles.push("Encourage breathing exercises and position changes first.");
            if (prefs.includes("No pain meds unless I ask")) roles.push("Advocate if staff offers medication unprompted.");
            if (prefs.includes("Hydrotherapy")) roles.push("Help her get in/out of tub safely.");
            return roles.join(" ");
        },
        labor: (prefs) => {
            const roles = ["Help her change positions every 30 minutes."];
            if (prefs.includes("Delayed cord clamping")) roles.push("Remind staff about delayed cord clamping.");
            if (prefs.includes("Intermittent monitoring")) roles.push("Ask about wireless/intermittent monitoring if continuous is suggested.");
            if (prefs.includes("Freedom to eat/drink")) roles.push("Keep light snacks and drinks available.");
            return roles.join(" ");
        },
        birth: (prefs) => {
            const roles = [];
            if (prefs.includes("Partner cuts cord")) roles.push("You'll cut the cord when ready - stay calm!");
            if (prefs.includes("Immediate skin-to-skin") || prefs.includes("Golden hour (1 hr bonding)")) {
                roles.push("Ensure uninterrupted skin-to-skin time.");
            }
            if (prefs.includes("Photographer/videographer")) roles.push("Take photos/video or direct the photographer.");
            if (prefs.includes("Gender reveal at birth")) roles.push("Announce the sex!");
            roles.push("Help initiate first feeding.");
            return roles.join(" ") || "Ensure skin-to-skin happens. Take photos. Help with first feeding.";
        },
        baby: () => "Stay with baby for any procedures. Ask questions. Handle paperwork.",
        family: (prefs) => {
            const roles = ["Be the gatekeeper for visitors."];
            if (prefs.includes("No visitors until ready")) roles.push("Communicate 'no visitors yet' to family firmly.");
            if (prefs.includes("Doula present")) roles.push("Coordinate with doula on supporting roles.");
            return roles.join(" ");
        },
        csection: (prefs) => {
            const roles = ["Stay calm. Sit by her head and hold her hand."];
            if (prefs.includes("Clear drape to see baby")) roles.push("Ask staff to lower drape when baby emerges.");
            if (prefs.includes("Immediate skin-to-skin in OR")) roles.push("Advocate for skin-to-skin in OR.");
            if (prefs.includes("Partner stays with baby")) roles.push("Go with baby if separated.");
            if (prefs.includes("Narration of procedure")) roles.push("Describe what you see when baby is born.");
            return roles.join(" ");
        },
        special: () => "Know the protocols. Be ready to advocate. Keep notes of medical discussions."
    };

    return roleTemplates[sectionId]?.(preferences) || "Be present and supportive.";
};

// --- HELPER FUNCTIONS ---

function getBabySize(week: number, theme: string): string {
    const sizes = BABY_SIZES[theme] || BABY_SIZES['fruit'];
    // Map week 4-42 to size array (roughly)
    const index = Math.min(Math.max(0, Math.floor((week - 4) / 1.3)), sizes.length - 1);
    return week < 4 ? "Microscopic" : sizes[index];
}

// --- EXPORTED SERVICE FUNCTIONS ---

export const generateDailyGuidance = async (
    week: number,
    partnerName: string,
    role: 'partner' | 'pregnant',
    checkIn?: DailyCheckIn,
    themePreference: string = 'fruit'
): Promise<DailyGuidance> => {
    // Simulate async for UI consistency
    await new Promise(resolve => setTimeout(resolve, 300));

    const babySize = getBabySize(week, themePreference);
    const partnerTip = getPartnerTipForWeek(week);
    const pregnancyTip = getPregnancyTipForWeek(week);

    let actionItem = "";
    let script = "";
    let selfCare = "";
    let theme = "";
    let context = "";

    if (role === 'partner' && partnerTip) {
        theme = partnerTip.title;
        context = partnerTip.mainTip;

        // Select action based on day of week for variety
        const dayOfWeek = new Date().getDay();

        // Symptom-aware selection
        if (checkIn?.partnerSymptoms.includes('Nausea') && week < 14) {
            actionItem = partnerTip.doThis.find(d =>
                d.toLowerCase().includes('smell') ||
                d.toLowerCase().includes('cook') ||
                d.toLowerCase().includes('food')
            ) || partnerTip.doThis[0];
        } else if (checkIn?.partnerSymptoms.includes('Back Pain')) {
            actionItem = partnerTip.doThis.find(d =>
                d.toLowerCase().includes('back') ||
                d.toLowerCase().includes('massage') ||
                d.toLowerCase().includes('rub')
            ) || partnerTip.doThis[0];
        } else if (checkIn?.partnerSymptoms.includes('Fatigue') || checkIn?.partnerSymptoms.includes('Insomnia')) {
            actionItem = partnerTip.doThis.find(d =>
                d.toLowerCase().includes('sleep') ||
                d.toLowerCase().includes('rest') ||
                d.toLowerCase().includes('bed')
            ) || partnerTip.doThis[0];
        } else if (checkIn?.partnerSymptoms.includes('Anxiety')) {
            actionItem = partnerTip.doThis.find(d =>
                d.toLowerCase().includes('worry') ||
                d.toLowerCase().includes('listen') ||
                d.toLowerCase().includes('talk')
            ) || partnerTip.doThis[0];
        } else {
            // Rotate through doThis based on day
            actionItem = partnerTip.doThis[dayOfWeek % partnerTip.doThis.length];
        }

        script = partnerTip.sayThis;
        selfCare = partnerTip.selfCare;
    } else if (role === 'pregnant' && pregnancyTip) {
        theme = pregnancyTip.title;
        context = pregnancyTip.bodyUpdate;

        if (checkIn?.myState === 'Anxious' || checkIn?.myState === 'Stressed') {
            actionItem = "Take a slow breath. Place a hand on your bump and feel your baby. This moment is real and you are doing great.";
            script = `"I'm feeling overwhelmed. Can we just talk for 10 minutes without problem-solving?"`;
        } else if (checkIn?.partnerSymptoms.includes('Nausea')) {
            actionItem = "Eat small amounts often. Ginger or lemon can help. Crackers before getting out of bed in the morning.";
            script = `"I need some bland food options today. Can you help with that?"`;
        } else if (checkIn?.partnerSymptoms.includes('Fatigue')) {
            actionItem = pregnancyTip.selfCareTip;
            script = `"I need to rest this evening. Can you handle dinner?"`;
        } else {
            actionItem = pregnancyTip.selfCareTip;
            script = `"I could really use some extra help today."`;
        }

        selfCare = "Drink a full glass of water right now. Take 5 slow, deep breaths. You're doing amazing.";
    } else {
        // Fallback
        if (role === 'partner') {
            theme = `Week ${week} Support`;
            context = "Every week brings new changes. Stay present and be ready to adapt.";
            actionItem = "Do one thing she usually handles without being asked. Small gestures matter.";
            script = `"What's one thing I can take off your plate today?"`;
            selfCare = "Take 10 minutes for yourself. You can't pour from an empty cup.";
        } else {
            theme = `Week ${week}`;
            context = "Your body is doing incredible work. Be patient with yourself.";
            actionItem = "Take a moment to connect with your baby. Hand on bump, deep breath.";
            script = `"I need a hug and some quiet time today."`;
            selfCare = "Put your feet up for 15 minutes. Literally.";
        }
    }

    return {
        week,
        theme,
        context,
        actionItem,
        script,
        selfCare,
        babyDev: pregnancyTip?.babyUpdate || (week < 12
            ? "Baby is forming vital organs and just starting to move."
            : week < 24
                ? "Baby is developing fingerprints, hair, and can hear sounds."
                : "Baby is practicing breathing and packing on weight for birth."),
        babySize: `Size of a ${babySize}`
    };
};

export const getGeneralCoachResponse = async (
    input: string,
    week: number,
    partnerName: string,
    role: 'partner' | 'pregnant',
    history: { role: 'user' | 'model', text: string }[]
): Promise<string> => {
    await new Promise(resolve => setTimeout(resolve, 400));

    const lowerInput = input.toLowerCase();

    if (role === 'partner') {
        // Partner-specific responses
        if (lowerInput.includes("crying") || lowerInput.includes("upset") || lowerInput.includes("emotional")) {
            return "Don't try to 'fix' it. First, just be present. Sit next to her, hand on back, and say: 'I know it's hard. I'm right here.' Ask if she wants a solution or just a hug. Usually it's the hug.";
        }
        if (lowerInput.includes("useless") || lowerInput.includes("can't help") || lowerInput.includes("don't know what to do")) {
            return "You're not useless - you're learning a new role. The best partners handle logistics: clean the kitchen, fill her water bottle, take out trash. Ask: 'What's one thing I can take off your plate?'";
        }
        if (lowerInput.includes("nausea") || lowerInput.includes("sick") || lowerInput.includes("morning sickness")) {
            return "Be the gatekeeper of smells. No strong cooking, perfumes, or cleaning products. Keep ginger ale/tea and bland crackers stocked. Don't take it personally if she can't eat what you made.";
        }
        if (lowerInput.includes("tired") || lowerInput.includes("exhausted") || lowerInput.includes("fatigue")) {
            return "Her fatigue is real - her body is working 24/7. Take over evening chores without being asked. Encourage early bedtime. Handle morning duties so she can sleep in when possible.";
        }
        if (lowerInput.includes("sex") || lowerInput.includes("intimate") || lowerInput.includes("physical")) {
            return "Libido changes are normal for both of you. Don't pressure, but do maintain physical affection - hand holding, cuddling, back rubs. Intimacy without expectation builds connection.";
        }
        if (lowerInput.includes("scared") || lowerInput.includes("anxious") || lowerInput.includes("worried")) {
            return "Your fears are valid. Partners worry too. Share your concerns with each other, but avoid dumping all your anxiety on her. Find a friend, therapist, or support group for your own processing.";
        }
        if (lowerInput.includes("labor") || lowerInput.includes("birth") || lowerInput.includes("delivery")) {
            return "Your job in labor: Be calm, be present, be her advocate. Learn the birth plan. Pack YOUR bag too. Practice counter-pressure massage. Remember: she needs you steady, not panicked.";
        }
        return `That's a great question. At week ${week}, focus on being consistent and present. Small, daily gestures of support matter more than grand gestures. What specific situation are you dealing with?`;
    } else {
        // Pregnant person responses
        if (lowerInput.includes("scared") || lowerInput.includes("anxious") || lowerInput.includes("worry")) {
            return "Your worries are completely valid. Take a deep breath. You're doing everything right by caring so much. Have you shared this specific fear with your partner or provider? Sometimes voicing it helps.";
        }
        if (lowerInput.includes("tired") || lowerInput.includes("exhausted") || lowerInput.includes("fatigue")) {
            return "Listen to your body. You're growing a whole human - that's marathon-level work happening inside you. Rest is not laziness, it's necessary. Can you take 20 minutes right now to lie down?";
        }
        if (lowerInput.includes("pain") || lowerInput.includes("hurt") || lowerInput.includes("uncomfortable")) {
            return "I'm sorry you're uncomfortable. Try changing positions, a warm (not hot) bath, or a heating pad on low. If the pain is sharp, rhythmic, or concerning, call your provider.";
        }
        if (lowerInput.includes("nausea") || lowerInput.includes("sick")) {
            return "Nausea is so draining. Eat small amounts frequently - empty stomach makes it worse. Ginger, lemon, crackers by the bed. This phase usually eases by week 12-14. You're getting through it.";
        }
        if (lowerInput.includes("partner") || lowerInput.includes("husband") || lowerInput.includes("boyfriend")) {
            return "Partners often struggle to know how to help. Try giving specific requests: 'Can you handle dinner tonight?' or 'I need you to just listen, not fix.' Direct communication helps them step up.";
        }
        if (lowerInput.includes("body") || lowerInput.includes("weight") || lowerInput.includes("changes")) {
            return "Your body is doing exactly what it needs to do. Weight gain is necessary and healthy. Every body carries differently. Focus on how you feel, not the numbers. You're incredible.";
        }
        return `I hear you. Pregnancy is a marathon with ups and downs. At week ${week}, focus on one day at a time. Be gentle with yourself. What do you need most right now?`;
    }
};

export const getLaborCoachResponse = async (
    input: string,
    stage: string,
    history: { role: 'user' | 'model', text: string }[]
): Promise<string> => {
    await new Promise(resolve => setTimeout(resolve, 300));

    const lowerInput = input.toLowerCase();

    if (lowerInput.includes("pain") || lowerInput.includes("hurt") || lowerInput.includes("intense")) {
        return "Breathe. Low moan on the exhale. Loose jaw, loose shoulders. You are stronger than the wave. Ride it out.";
    }
    if (lowerInput.includes("breathe") || lowerInput.includes("breath") || lowerInput.includes("breathing")) {
        return "Slow breath in through the nose for 4 counts. Out through the mouth for 4 counts. Eyes soft. Jaw loose. You've got this.";
    }
    if (lowerInput.includes("back") || lowerInput.includes("pressure")) {
        return "Counter-pressure helps. Push firmly on the lower back or hips during contractions. Try hands and knees position for back labor.";
    }
    if (lowerInput.includes("scared") || lowerInput.includes("can't")) {
        return "You CAN do this. You ARE doing this. One contraction at a time. Your body knows what to do. Trust yourself.";
    }
    if (lowerInput.includes("how long") || lowerInput.includes("when")) {
        return "Focus only on this moment, this breath, this contraction. Don't think ahead. You're doing it right now. That's all that matters.";
    }

    // Stage-specific responses
    switch (stage) {
        case 'early':
            return "Early labor can be long. Distract, distract, distract. Watch a movie. Walk around. Eat light snacks. Save energy for later.";
        case 'active':
            return "You're in the thick of it now. Focus on breath. 4 in, 4 out. Change positions every 30 minutes. You're making progress with every contraction.";
        case 'transition':
            return "Transition is the hardest and the SHORTEST part. You're almost there. Eye contact. Short words: 'Breathe. You're safe. I'm here.' The end is near.";
        case 'pushing':
            return "Listen to your body's urge to push. Chin to chest. Deep breath, bear down. Rest completely between contractions. Baby is coming!";
        default:
            return "Stay calm. You are her anchor. Be steady, be present. Your calm is contagious.";
    }
};

export const getCommunitySupport = async (message: string, week: number): Promise<string> => {
    await new Promise(resolve => setTimeout(resolve, 600));

    const responses = [
        "Totally normal! I felt the exact same way around this week.",
        "Hang in there! The second trimester usually brings more energy.",
        "Have you tried ginger tea? It worked wonders for me.",
        "Sending good vibes your way! You're doing amazing.",
        "Same here! You're definitely not alone.",
        "My partner said the exact same thing yesterday 😂",
        "Just focus on getting through today. One day at a time.",
        "This phase is temporary! It gets better, I promise.",
        "You've got this! We're all rooting for you.",
        "Totally valid feelings. Pregnancy is a wild ride."
    ];
    return responses[Math.floor(Math.random() * responses.length)];
};

export const generateMilestoneSupport = async (
    milestoneName: string,
    week: number,
    role: 'partner' | 'pregnant',
    partnerName: string
): Promise<MilestoneInfo> => {
    await new Promise(resolve => setTimeout(resolve, 300));

    // First try to get from the comprehensive milestone array (constants/milestoneData.ts)
    const detailedMilestone = getFullMilestoneData(week, milestoneName);

    if (detailedMilestone) {
        return {
            title: detailedMilestone.title,
            description: detailedMilestone.description,
            partnerTip: role === 'partner' ? detailedMilestone.partnerTip : detailedMilestone.pregnantTip,
            logistics: detailedMilestone.logistics
        };
    }

    // Fallback to local MILESTONE_DATA for fuzzy matching
    const key = Object.keys(MILESTONE_DATA).find(k =>
        milestoneName.toLowerCase().includes(k.toLowerCase()) ||
        k.toLowerCase().includes(milestoneName.toLowerCase().slice(0, 10))
    );

    const base = key ? MILESTONE_DATA[key] : {
        title: milestoneName,
        description: "An important milestone in your pregnancy journey.",
        partnerTip: "Be present and supportive. Take photos if appropriate.",
        logistics: "Check with your healthcare provider for specific details."
    };

    // Customize based on role
    if (role === 'pregnant') {
        return {
            ...base,
            partnerTip: "This is a great moment to pause and reflect on how far you've come. Share your feelings with your partner."
        };
    }

    return base;
};

// Re-export milestone data for other components that need direct access
export { MILESTONE_ARRAY, getFullMilestoneData };

export const generateHospitalBagList = async (week: number, season: string): Promise<ChecklistItem[]> => {
    await new Promise(resolve => setTimeout(resolve, 400));

    let items = [...HOSPITAL_BAG_ITEMS];

    // Add seasonal items
    if (season === 'Winter' || season === 'Fall') {
        items.push(
            { id: 'seasonal-1', text: "Warm blanket for baby", category: 'baby', isChecked: false, addedBy: 'ai' },
            { id: 'seasonal-2', text: "Warm hat for baby", category: 'baby', isChecked: false, addedBy: 'ai' },
            { id: 'seasonal-3', text: "Warm socks for you", category: 'mom', isChecked: false, addedBy: 'ai' },
            { id: 'seasonal-4', text: "Warm jacket for going home", category: 'mom', isChecked: false, addedBy: 'ai' }
        );
    } else if (season === 'Summer') {
        items.push(
            { id: 'seasonal-1', text: "Light swaddle/muslin blanket", category: 'baby', isChecked: false, addedBy: 'ai' },
            { id: 'seasonal-2', text: "Portable fan for labor", category: 'partner', isChecked: false, addedBy: 'ai' },
            { id: 'seasonal-3', text: "Light cotton going-home outfit", category: 'mom', isChecked: false, addedBy: 'ai' }
        );
    }

    return items;
};

export const checkFoodSafety = async (foodItem: string): Promise<FoodSafetyResult> => {
    await new Promise(resolve => setTimeout(resolve, 200));

    const lowerItem = foodItem.toLowerCase().trim();

    // Direct match
    if (FOOD_DATABASE[lowerItem]) {
        return { ...FOOD_DATABASE[lowerItem], item: foodItem };
    }

    // Partial match
    const matchKey = Object.keys(FOOD_DATABASE).find(k =>
        lowerItem.includes(k) || k.includes(lowerItem)
    );

    if (matchKey) {
        return { ...FOOD_DATABASE[matchKey], item: foodItem };
    }

    // Heuristic fallbacks
    if (lowerItem.includes("raw") || lowerItem.includes("uncooked")) {
        return {
            item: foodItem,
            status: "Caution",
            reason: "Raw or undercooked foods generally carry higher risks during pregnancy. Cook thoroughly when possible.",
            alternatives: "Fully cooked versions of this food."
        };
    }

    if (lowerItem.includes("pasteurized")) {
        return {
            item: foodItem,
            status: "Safe",
            reason: "Pasteurization kills harmful bacteria, making this safe to consume.",
            alternatives: ""
        };
    }

    return {
        item: foodItem,
        status: "Caution",
        reason: "We don't have specific information about this item. When in doubt, ensure it's fully cooked and from a reliable source.",
        alternatives: "Ask your healthcare provider if you're unsure."
    };
};

export const generateBabyNames = async (style: string, gender: string = 'neutral'): Promise<NameIdea[]> => {
    await new Promise(resolve => setTimeout(resolve, 400));

    let filtered = [...BABY_NAMES];

    // Filter by style
    if (style.toLowerCase().includes('classic')) {
        filtered = filtered.filter(n =>
            ['Elizabeth', 'Charlotte', 'Eleanor', 'Victoria', 'Grace', 'James', 'William', 'Henry', 'Charles', 'George', 'Benjamin', 'Thomas'].includes(n.name)
        );
    } else if (style.toLowerCase().includes('nature')) {
        filtered = filtered.filter(n =>
            ['River', 'Sage', 'Rowan', 'Wren', 'Willow', 'Ivy', 'Hazel', 'Juniper', 'Forrest', 'Aspen', 'Ocean'].includes(n.name)
        );
    } else if (style.toLowerCase().includes('unique') || style.toLowerCase().includes('uncommon')) {
        filtered = filtered.filter(n =>
            ['Zephyr', 'Orion', 'Seren', 'Indigo', 'Phoenix', 'Lyra', 'Atlas', 'Nova', 'Freya'].includes(n.name)
        );
    }

    // Filter by gender
    if (gender === 'boy') {
        filtered = filtered.filter(n => n.gender === 'boy' || n.gender === 'neutral');
    } else if (gender === 'girl') {
        filtered = filtered.filter(n => n.gender === 'girl' || n.gender === 'neutral');
    }

    // Shuffle and return 10
    return filtered.sort(() => Math.random() - 0.5).slice(0, 10);
};

export const generateAppointmentQuestions = async (week: number): Promise<QuestionItem[]> => {
    await new Promise(resolve => setTimeout(resolve, 300));

    let questions: QuestionItem[] = [];

    if (week < 14) {
        questions = [...APPOINTMENT_QUESTIONS.first];
    } else if (week < 28) {
        questions = [...APPOINTMENT_QUESTIONS.second];
    } else {
        questions = [...APPOINTMENT_QUESTIONS.third];
    }

    // Shuffle slightly
    return questions.sort(() => Math.random() - 0.3);
};

export const generateBirthPlanAdvice = async (preferences: string[]): Promise<BirthPlan> => {
    await new Promise(resolve => setTimeout(resolve, 400));

    // Group preferences by section
    const sectionPrefs: Record<string, string[]> = {};

    for (const pref of preferences) {
        const sectionId = PREFERENCE_TO_SECTION_MAP[pref];
        if (sectionId) {
            if (!sectionPrefs[sectionId]) sectionPrefs[sectionId] = [];
            sectionPrefs[sectionId].push(pref);
        }
    }

    // Build personalized sections - only include sections that have preferences
    const personalizedSections: BirthPlanSection[] = BIRTH_PLAN_SECTIONS_FULL
        .filter(section => {
            // Always include C-section and special if any preferences are set
            if (section.id === 'csection' || section.id === 'special') {
                return (sectionPrefs[section.id]?.length || 0) > 0;
            }
            // Include section if it has preferences, or add with defaults
            return true;
        })
        .map(section => {
            const prefs = sectionPrefs[section.id] || [];
            return {
                ...section,
                preferences: prefs.length > 0 ? prefs : getDefaultPrefsForSection(section.id),
                partnerRole: prefs.length > 0
                    ? generatePartnerRole(section.id, prefs)
                    : section.partnerRole
            };
        })
        .filter(section => section.preferences.length > 0);

    return { sections: personalizedSections };
};

// Default preferences for sections when none are selected
const getDefaultPrefsForSection = (sectionId: string): string[] => {
    const defaults: Record<string, string[]> = {
        env: ["Dim lighting", "Quiet atmosphere", "Freedom to move around"],
        pain: ["Natural methods first", "Epidural if requested"],
        labor: ["Delayed cord clamping", "Spontaneous pushing"],
        birth: ["Immediate skin-to-skin", "Golden hour (1 hr bonding)"],
        baby: ["Keep baby in room always"],
        family: ["Partner present throughout"],
        csection: [],
        special: []
    };
    return defaults[sectionId] || [];
};

export const generateFlashFacts = async (week: number, role: 'partner' | 'pregnant' = 'pregnant'): Promise<FlashFact[]> => {
    await new Promise(resolve => setTimeout(resolve, 200));

    // Use the comprehensive 500-fact database from constants/flashFacts.ts
    // Returns role-appropriate facts with variety
    return getFactsForWeekAndRole(week, role);
};

// Also export the full facts list for direct access if needed
export { ALL_FLASH_FACTS };

// Image analysis not supported without AI - return helpful message
export const analyzeImage = async (base64Image: string): Promise<{ category: string; safetyStatus: string; title: string; summary: string; actionableAdvice: string }> => {
    await new Promise(resolve => setTimeout(resolve, 500));

    return {
        category: 'unknown',
        safetyStatus: 'Info',
        title: "Image Analysis",
        summary: "Image scanning feature is not available. Please use the text search to look up specific foods or products.",
        actionableAdvice: "Type the name of the food or product in the search bar for safety information."
    };
};
